import { Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, Output, ViewChild } from '@angular/core';
import { debug } from 'console';
import * as d3 from 'd3';
import { Subscription } from 'rxjs';
import { DataAggregationService, AggregationConfig } from 'src/app/services/data-aggregation.service';
import { FilterService } from 'src/app/services/filter.service';

@Component({
  selector: 'app-dynamic-bar-chart',
  templateUrl: './dynamic-bar-chart.component.html',
  styleUrls: ['./dynamic-bar-chart.component.scss']
})

export class DynamicBarChartComponent implements OnInit, OnDestroy, OnChanges {

  @Input('data') data: any;
  @Input('item') item: any;
  @Input('pageKey') pageKey: any;
  @Input('config') config: any;
  @Input('headerConfig') headerConfig: any;
  @Input('error_name') error_name: any;
  @Input('heading') heading: any;
  @Output() barClicked = new EventEmitter<{ column: string; value: any }>();

  @ViewChild('fs', { static: true }) fs!: ElementRef;
  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
  @HostBinding('class.is-fullscreen') isFullscreen = false;
  isActive = false;
  isLoading: boolean = true;
  noData: boolean = false;
  iconList: any = [];
  initiateChart: boolean = false;
  chartData: any = [];
  props: any = {};
  private static instanceCounter = 0;
  divId: any = 'dynamicBarChart_' + (++DynamicBarChartComponent.instanceCounter);
  currentVisibleData: any = [];
  activeChart: string = 'stackedBarArea';
  activePeriod: string = 'M';
  // legend toggle state: hidden series keys
  hiddenKeys: Set<string> = new Set();
  // D3 range slider state — primary (left Y) and secondary (right Y)
  private handleRadius = 6;
  lineMin: number = 0;
  lineMax: number = 0;
  lineMinRight: number = 0;
  lineMaxRight: number = 0;
  orientation: string = 'vertical';
  private resizeObserver: ResizeObserver | null = null;
  private resizeTimeout: any = null;
  private resizeSkipFirst = true;
   subs: Subscription[] = [];

  constructor(private dataAggregation: DataAggregationService,private filterService: FilterService) {
    this.subs.push(
        this.filterService.filterQuery.subscribe((query: any) => {
          this.isLoading = true;
        }))
   }

  /**
   * Aggregate raw rows by `period` and compute the average business-hour TAT
   * (total_bi_tat_hours / total_bi_tat_req) per period plus an overall summary.
   *
   * Mirrors the spreadsheet formula =SUM(total_bi_tat_hours)/SUM(total_bi_tat_req).
   * Pass `scale: 60` via the second argument to convert hours → minutes.
   */
  getAvgBusinessHours(rows: any[], cfg: any) {
    const config: AggregationConfig = {
      groupBy:cfg['aggregation_groupBy'] ||'period',
      fields:cfg['aggregation_fields']||{
        total_bi_tat_hours :   { source: 'total_bi_tat_hours',    op: 'sum' },
        total_bi_tat_req   :   { source: 'total_bi_tat_req',      op: 'sum' },
        bi_tat_less_than_60:   { source: 'bi_tat_less_than_60',   op: 'sum' },
        bi_tat_less_than_8h:   { source: 'bi_tat_less_than_8h',   op: 'sum' },
        bi_tat_more_than_8h:   { source: 'bi_tat_more_than_8h',   op: 'sum' },
      },
      computed: cfg['aggregation_computed'] || {
        avg_bi_hours: {
          op: 'ratio',
          numerator: 'total_bi_tat_hours',
          denominator: 'total_bi_tat_req',
          scale:  1,
          round:  2,
        },
      },
    };
      console.log(`Chart Props:${cfg.heading}`,config);
    const result = this.dataAggregation.aggregate(rows, config);
    return { groups: result.groups, summary: result.summary };
  }

  @HostListener('fullscreenchange', ['$event'])
  @HostListener('webkitfullscreenchange', ['$event'])
  @HostListener('mozfullscreenchange', ['$event'])
  @HostListener('MSFullscreenChange', ['$event'])
  screenChange() {
   if (!document.fullscreenElement) {
      this.closeFullscreen();
    }
  }

   fullscreenChanges(fullscreen:any){
    if(fullscreen==false){
      this.closeFullscreen()
      return
    }
    this.props.chartHeight = window.outerHeight - 60;
    this.isFullscreen = fullscreen;
    this.isActive = fullscreen;
    this.plotChart()
}
  closeFullscreen(): void {
    this.props.chartHeight = Number(this.item?.config?.chartHeight) || 500;
    this.isFullscreen = false;
    this.isActive = false;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    setTimeout(() => {
      this.plotChart()
    }, 100);
  }

  private showTooltip(myType: any, myData: any, myX: any, myY: any, chartWidth: any): void {
    const tooltip = d3.select('#d3Tooltip' + this.divId);
    if (tooltip.empty()) return;

    // format date as title
    const date = myData.date ? new Date(myData.date) : null;
    let titleDate = '';
    if (date) {
      const monthNames = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
      titleDate = monthNames[date.getMonth()] + ' ' + date.getFullYear();
    }

    // chart title from heading input
    const chartTitle = (this.item.config.heading||this.heading || 'CHART').toUpperCase() + (titleDate ? ', ' + titleDate : '');

    // separate bar keys, line keys, and area keys
    const barKeys = (this.props.barKeys || []).filter((k: string) => !this.hiddenKeys.has(k));
    const lineKeys = (this.props.lineKeys || []).filter((k: string) => !this.hiddenKeys.has(k));
    const areaKeys = (this.props.areaKeys || []).filter((k: string) => !this.hiddenKeys.has(k));
    // area keys not already shown as line keys
    const areaOnlyKeys = areaKeys.filter((k: string) => lineKeys.indexOf(k) === -1);

    // build row helper
    const isPercentChart = (this.props.percentageMode || 'none') !== 'none';
    const primarySuffix = isPercentChart ? '%' : (this.props.yAxisSuffix || '');
    const secondarySuffix = this.props.yAxisRightSuffix || '';
    // determine which keys are on the secondary (right) Y-axis
    const isStackedAreaTooltip = this.props.stackedArea === true;
    const lineOnPrimaryTooltip = this.props.lineOnPrimaryAxis === true;
    const secondaryKeySet = new Set(
      (isStackedAreaTooltip || lineOnPrimaryTooltip) ? [] : [...(this.props.lineKeys || []), ...(this.props.areaKeys || []).filter((k: string) => (this.props.lineKeys || []).indexOf(k) === -1)]
    );
    const buildRow = (key: string, val: any): string => {
      if (val === null || val === undefined) return '';
      const colour = this.props.colours[key] || '#fff';
      const label = (this.props.legendLabels[key] || key).toUpperCase();
      const isSecondary = secondaryKeySet.has(key);
      const suffix = isSecondary ? secondarySuffix : primarySuffix;
      let formatted: string;
      if (typeof val !== 'number') {
        formatted = val;
      } else if (isPercentChart && !isSecondary) {
        formatted = d3.format(',.1f')(val) + '%';
      } else {
        formatted = (val < 1 && val > 0 ? val.toFixed(1) : (Number.isInteger(val) ? d3.format(',')(val) : d3.format(',.1f')(val))) + suffix;
      }
      return '<div style="display:flex;align-items:center;gap:6px;padding:3px 0">' +
        '<span style="width:8px;height:8px;border-radius:50%;background:' + colour + ';flex-shrink:0"></span>' +
        '<span style="flex:1;font-weight:500;font-size:9px;letter-spacing:0.2px;font-family:Poppins,sans-serif">' + label + '</span>' +
        '<span style="font-weight:600;margin-left:8px;font-size:9px;white-space:nowrap;font-family:Poppins,sans-serif">' + formatted + '</span></div>';
    };

    let html = '';
    // title header
    html += '<div style="padding:5px 10px;border-bottom:1px solid #333;font-weight:600;font-size:10px;letter-spacing:0.3px;color:#fff;font-family:Poppins,sans-serif">' + chartTitle + '</div>';

    // bar keys section
    if (barKeys.length > 0) {
      html += '<div style="padding:3px 10px;background:#2A2A2A">';
      barKeys.forEach((key: string) => { html += buildRow(key, myData[key]); });
      html += '</div>';
    }

    // line keys section (separated)
    if (lineKeys.length > 0) {
      html += '<div style="padding:3px 10px;' + (barKeys.length > 0 ? 'border-top:1px solid #444;' : '') + 'background:#333">';
      lineKeys.forEach((key: string) => { html += buildRow(key, myData[key]); });
      html += '</div>';
    }

    // area-only keys section (area keys not already shown as line keys)
    if (areaOnlyKeys.length > 0) {
      html += '<div style="padding:3px 10px;' + (barKeys.length > 0 || lineKeys.length > 0 ? 'border-top:1px solid #444;' : '') + 'background:#333">';
      areaOnlyKeys.forEach((key: string) => { html += buildRow(key, myData[key]); });
      html += '</div>';
    }

    // position: auto-adjust relative to parent container (both axes)
    tooltip.html(html);

    const containerRect = this.fs.nativeElement.getBoundingClientRect();
    const tooltipNode = tooltip.node() as HTMLElement;
    const tooltipW = tooltipNode.offsetWidth || 260;
    const tooltipH = tooltipNode.offsetHeight || 150;
    const containerW = containerRect.width;
    const containerH = containerRect.height;
    const gap = 12;

    const relX = myX - containerRect.left;
    const relY = myY - containerRect.top;

    // Horizontal: prefer right, flip left if overflows
    let left = relX + gap;
    if (left + tooltipW > containerW) {
      left = relX - tooltipW - gap;
    }
    // Clamp within container
    left = Math.max(0, Math.min(left, containerW - tooltipW));

    // Vertical: prefer above cursor, flip below if overflows
    let top = relY - tooltipH - gap;
    if (top < 0) {
      top = relY + gap;
    }
    // Clamp within container
    top = Math.max(0, Math.min(top, containerH - tooltipH));

    tooltip
      .style('left', left + 'px')
      .style('top', top + 'px')
      .style('opacity', '1')
      .style('transform', 'translateY(0)');
  }

  private hideTooltip(myType: any): void {
    d3.select('#d3Tooltip' + this.divId)
      .style('opacity', '0')
      .style('transform', 'translateY(6px)');
  }

  // data change event
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && changes['data'].currentValue != changes['data'].previousValue && this.initiateChart) {
      // Remove old SVG and re-create to avoid duplicates
      this.isLoading = true;
      this.getChartData();
    }
  }

  ngOnInit(): void {
    this.iconList = this.item.config.icon ? this.item.config.icon : this.iconList
    this.divId = this.item.config.divId + this.item.widget_uniq_key
    this.initiateCharts();
    this.setupResizeObserver();
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    d3.select('#d3Tooltip' + this.divId).remove();
  }

  private setupResizeObserver(): void {
    const container = this.chartContainer?.nativeElement;
    if (container) {
      this.resizeObserver = new ResizeObserver(() => {
        // skip the first callback — browser fires it on initial observe,
        // but getChartData() already renders the chart with animation
        if (this.resizeSkipFirst) {
          this.resizeSkipFirst = false;
          return;
        }
        if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          if (this.chartData.length > 0 && this.props.chartHeight) {
            d3.select(this.chartContainer.nativeElement).select('svg').remove();
            this.initiateCharts();
            this.plotChart();
          }
        }, 200);
      });
      this.resizeObserver.observe(container);
    }
  }

  initiateCharts(): void {
    const myChart = this;
    const myClass = myChart.divId;

    const mySvg = d3.select(this.chartContainer.nativeElement)
      .append('svg')
      .attr('id', 'svg_' + myClass)
      .attr('width', '100%')
      .style('background-color', 'white');

    // no data message
    mySvg.append('text').attr('id', 'noDataMessage' + myClass);
    const defs = mySvg.append('defs');
    // brush clip path
    defs.append('clipPath').attr('id', 'brushClip' + myClass)
      .append('rect').attr('id', 'brushClipRect' + myClass);
    // drop shadow filter
    const filter = defs.append('filter').attr('id', 'drop-shadow-' + myClass).attr('width', 10).attr('height', 24);
    filter.append('feGaussianBlur').attr('in', 'SourceAlpha').attr('stdDeviation', 1).attr('result', 'blur');
    filter.append('feOffset').attr('in', 'blur').attr('dx', 1)
      .attr('dy', 1).attr('result', 'offsetBlur');
    filter.append('feFlood').attr('in', 'offsetBlur')
      .attr('flood-color', '#000000').attr('flood-opacity', 0.4)
      .attr('result', 'offsetColor');
    filter.append('feComposite').attr('in', 'offsetColor').attr('in2', 'offsetBlur').attr('operator', 'in').attr('result', 'offsetBlur');
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'offsetBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
    // area gradient
    const areaGradient = defs.append('linearGradient')
      .attr('id', 'areaGradient' + myClass)
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '0%').attr('y2', '100%');
    areaGradient.append('stop').attr('offset', '0%').attr('stop-color', '#FFDCC8').attr('stop-opacity', 0.8);
    areaGradient.append('stop').attr('offset', '100%').attr('stop-color', '#FFDCC8').attr('stop-opacity', 0.15);
    // axes
    mySvg.append('g').attr('id', 'xAxis' + myClass).attr('class', 'axis' + myClass);
    mySvg.append('g').attr('id', 'yAxis' + myClass).attr('class', 'axis' + myClass);
    mySvg.append('g').attr('id', 'yAxisRight' + myClass).attr('class', 'axis' + myClass);
    // partial period line
    mySvg.append('line').attr('class', 'partialPeriodLine' + myClass);
    mySvg.append('text').attr('class', 'partialPeriodLineLabel' + myClass);
    // axis labels
    mySvg.append('text').attr('id', 'xAxisLabel' + myClass);
    mySvg.append('text').attr('id', 'yAxisLabel' + myClass);
    mySvg.append('text').attr('id', 'yAxisRightLabel' + myClass);
    // chart group
    mySvg.append('g').attr('id', 'chartGroup' + myClass);
    // brush group
    mySvg.append('g').attr('id', 'brushGroup' + myClass);
    mySvg.append('g').attr('id', 'brushSelectionGroup' + myClass);
    // handle lines
    mySvg.append('line').attr('class', 'handleLines' + myClass + ' handleLeftLine1' + myClass);
    mySvg.append('line').attr('class', 'handleLines' + myClass + ' handleLeftLine2' + myClass);
    mySvg.append('line').attr('class', 'handleLines' + myClass + ' handleRightLine1' + myClass);
    mySvg.append('line').attr('class', 'handleLines' + myClass + ' handleRightLine2' + myClass);
    // legend group (dynamic)
    mySvg.append('g').attr('id', 'legendGroup' + myClass);
    // hover guide line (vertical)
    mySvg.append('line').attr('id', 'hoverLine' + myClass);
    // hover crosshair lines (horizontal) — one per key, plus value labels
    mySvg.append('g').attr('id', 'hoverCrosshairGroup' + myClass)
      .attr('pointer-events', 'none');
    // slider group (zoom slider)
    mySvg.append('g').attr('id', 'sliderGroup' + myClass).attr('class', 'slider' + myClass);
    // tooltip div (append inside the fullscreen-able article so it's visible in fullscreen)
    d3.select('#d3Tooltip' + myClass).remove();
    d3.select(this.fs.nativeElement).append('div')
      .attr('id', 'd3Tooltip' + myClass)
      .style('position', 'absolute')
      .style('padding', '0')
      .style('background', '#1E1E1E')
      .style('color', '#fff')
      .style('border-radius', '8px')
      .style('font-family', 'Poppins')
      .style('font-size', '10px')
      .style('pointer-events', 'none')
      .style('opacity', '0')
      .style('z-index', '9999')
      .style('box-shadow', '0 4px 12px rgba(0,0,0,0.3)')
      .style('overflow', 'hidden')
      .style('min-width', '180px')
      .style('max-width', '220px')
      .style('transition', 'opacity 0.15s ease, left 0.12s ease, top 0.12s ease, transform 0.15s ease')
      .style('transform', 'translateY(6px)');
    this.initiateChart = true;
  }

  plotChart(): void {
    const myChart = this;
    const myClass = myChart.divId;
    const mySvg: any = d3.select('#svg_' + myClass);
    const width: any = mySvg.node().getBoundingClientRect().width;
    const height = myChart.props.chartHeight;
    const lineDot = myChart.props.lineDot || {};
    const circleRadius = lineDot.radius != null ? lineDot.radius : 4;
    const dotFill = lineDot.fill || 'white';
    const dotStrokeWidth = lineDot.strokeWidth != null ? lineDot.strokeWidth : 2;
    const dotShow = lineDot.show !== false;
    const dataLabels = myChart.props.dataLabels || {};
    const dlShowBar = dataLabels.bar === true;
    const dlShowLine = dataLabels.line === true;
    const dlShowArea = dataLabels.area === true;
    const dlShowTotal = dataLabels.total === true;
    const dlShowValue = dataLabels.value !== false;
    const dlFontSize = dataLabels.fontSize || 11;
    const dlFontWeight = dataLabels.fontWeight || 600;
    const dlTotalFontWeight = dataLabels.totalFontWeight || 700;
    const dlColor = dataLabels.color || '#333';
    const dlTotalColor = dataLabels.totalColor || dlColor;
    const dlFormat = dataLabels.format || '-2~s';
    mySvg.attr('height', height);

    let margins: any = { left: 75, right: 65, top: 60, bottom: 10, brush: 52, mid: 80 };
    if (myChart.props.showBrush === false) {
      margins.brush = 0;
      margins.mid = 50;
      margins.bottom = 0;
    }
    const chartHeight = height - margins.top - margins.bottom - margins.brush - margins.mid;
    const plotWidth = width - margins.left - margins.right;

    // data period is always derived from the data (monthly), display format follows activePeriod
    const dataPeriod = 'M';  // data grouping/offset always monthly
    const timeConverter: any = { "D": d3.timeDay, "W": d3.timeWeek, "M": d3.timeMonth, "Q": d3.timeMonth, "Y": d3.timeYear };
    const tickFormatMap: any = {
      "D": "%d %b %Y",
      "W": "%d %b %Y",
      "M": "%b\n%Y",
      "Q": "Q" + "%q %Y",  // fallback below
      "Y": "%Y"
    };
    const displayPeriod = myChart.activePeriod || 'M';
    // custom format function for display period
    const xTickFormat: any = (d: any) => {
      const date = new Date(d);
      if (displayPeriod === 'D') return d3.timeFormat('%d %b %Y')(date);
      if (displayPeriod === 'W') return d3.timeFormat('%d %b %Y')(date);
      if (displayPeriod === 'Q') {
        const q = Math.ceil((date.getMonth() + 1) / 3);
        return 'Q' + q + ' ' + d3.timeFormat('%Y')(date);
      }
      if (displayPeriod === 'Y') return d3.timeFormat('%Y')(date);
      return d3.timeFormat('%b\n%Y')(date);  // default M
    };

    // --- DATA TRANSFORMATION ---
    let chartData: any = JSON.parse(JSON.stringify(myChart.chartData));
    chartData.map((m: any) => m.date = new Date(m[myChart.props.xAxisVar]));
    let dateGroup = Array.from(d3.group(chartData, (g: any) => g.date));
    dateGroup = dateGroup.sort((a: any, b: any) => d3.ascending(a[0], b[0]));
    const dateGroupDates: any = [];
    chartData = [];

    // all keys (used for legend display)
    const allBarKeys = myChart.props.barKeys;
    const allLineKeys = myChart.props.lineKeys;
    const allAreaKeys = myChart.props.areaKeys;
    // visible keys (filtered by legend toggle — used for data, scales, rendering)
    const barKeys = allBarKeys.filter((k: string) => !myChart.hiddenKeys.has(k));
    const lineKeys = allLineKeys.filter((k: string) => !myChart.hiddenKeys.has(k));
    const areaKeys = allAreaKeys.filter((k: string) => !myChart.hiddenKeys.has(k));
    const barMode = myChart.props.barMode;         // 'clustered' | 'stacked'

    const isStackedArea = myChart.props.stackedArea === true;
    const percentageMode: string = myChart.props.percentageMode || 'none'; // 'none' | 'ofMax' | 'ofTotal' | 'actual'
    const isPercentageChart = percentageMode !== 'none';
    const isPercentAxis = isPercentageChart;

    dateGroup.forEach((d: any) => {
      dateGroupDates.push(d[0]);
      const row: any = { date: d[0] };
      // aggregate ALL keys (including hidden) so axes stay stable on legend toggle
      allBarKeys.forEach((key: string) => {
        row[key] = d3.sum(d[1], (s: any) => s[key]);
      });
      // aggregate line/area keys (all, including hidden)
      allLineKeys.forEach((key: string) => {
        row[key] = d3.mean(d[1], (s: any) => s[key]);
      });
      allAreaKeys.forEach((key: string) => {
        if (allLineKeys.indexOf(key) === -1) {
          row[key] = d3.mean(d[1], (s: any) => s[key]);
        }
      });
      // for stacked bars: compute cumulative y positions (visible keys only for rendering)
      if (barMode === 'stacked') {
        let cumulative = 0;
        barKeys.forEach((key: string) => {
          row[key + '_y0'] = cumulative;
          cumulative += row[key];
          row[key + '_y1'] = cumulative;
        });
        row._stackTotal = cumulative;
        // compute full stack total (all keys) for stable axis scaling
        let cumulativeAll = 0;
        allBarKeys.forEach((key: string) => { cumulativeAll += row[key] || 0; });
        row._stackTotalAll = cumulativeAll;
      }
      // for stacked areas: compute cumulative y positions
      if (isStackedArea) {
        let cumulative = 0;
        areaKeys.forEach((key: string) => {
          row[key + '_y0'] = cumulative;
          cumulative += row[key];
          row[key + '_y1'] = cumulative;
        });
        row._areaStackTotal = cumulative;
      }
      chartData.push(row);
    });

    // --- PERCENTAGE CONVERSION ---
    // Only apply to primary axis keys (bars); secondary axis keys (lines/areas) keep normal values
    if (percentageMode === 'ofMax') {
      // Legacy: convert each value to percentage of the global max across all bar keys
      const primaryKeys = [...new Set([...allBarKeys])];
      let rawMax = 0;
      chartData.forEach((row: any) => {
        primaryKeys.forEach((key: string) => {
          if (row[key] != null && row[key] > rawMax) rawMax = row[key];
        });
      });
      if (rawMax > 0) {
        chartData.forEach((row: any) => {
          primaryKeys.forEach((key: string) => {
            if (row[key] != null) {
              row[key] = (row[key] / rawMax) * 100;
            }
          });
          if (barMode === 'stacked') {
            let cumulative = 0;
            barKeys.forEach((key: string) => {
              row[key + '_y0'] = cumulative;
              cumulative += row[key];
              row[key + '_y1'] = cumulative;
            });
            row._stackTotal = cumulative;
          }
        });
      }
    } else if (percentageMode === 'ofTotal') {
      // Normalized stacking: each bar key value = value / row total * 100
      // Every bar sums to 100% per period
      chartData.forEach((row: any) => {
        let rowTotal = 0;
        allBarKeys.forEach((key: string) => { rowTotal += (row[key] || 0); });
        if (rowTotal > 0) {
          allBarKeys.forEach((key: string) => {
            if (row[key] != null) {
              row[key] = (row[key] / rowTotal) * 100;
            }
          });
        }
        // recalculate stacked positions
        if (barMode === 'stacked') {
          let cumulative = 0;
          barKeys.forEach((key: string) => {
            row[key + '_y0'] = cumulative;
            cumulative += row[key] || 0;
            row[key + '_y1'] = cumulative;
          });
          row._stackTotal = cumulative;
          let cumulativeAll = 0;
          allBarKeys.forEach((key: string) => { cumulativeAll += row[key] || 0; });
          row._stackTotalAll = cumulativeAll;
        }
      });
    } else if (percentageMode === 'actual') {
      // Data already contains percentage values — display as-is, no conversion needed
      // Just recalculate stacked positions if stacked bars
      if (barMode === 'stacked') {
        chartData.forEach((row: any) => {
          let cumulative = 0;
          barKeys.forEach((key: string) => {
            row[key + '_y0'] = cumulative;
            cumulative += row[key] || 0;
            row[key + '_y1'] = cumulative;
          });
          row._stackTotal = cumulative;
        });
      }
    }

    // --- SCALES ---
    const tickValues = myChart.props.d3AxisFormatting === true ? null : dateGroupDates;
    const tickCount = myChart.props.d3AxisFormatting === true ? 6 : null;

    // lineOnPrimaryAxis: lines/areas use left Y-axis (no right axis)
    const lineOnPrimary = myChart.props.lineOnPrimaryAxis === true;

    // yMax for bar axis (left) — use ALL keys so axes stay stable on legend toggle
    let yMaxBar: any;
    if (isPercentAxis && percentageMode === 'actual' && barMode === 'stacked') {
      // 'actual' mode with stacked bars: derive max from data (stack total may exceed 100)
      yMaxBar = d3.max(chartData, (m: any) => m._stackTotalAll || m._stackTotal) || 100;
    } else if (isPercentAxis) {
      yMaxBar = 100;
    } else if (isStackedArea) {
      yMaxBar = d3.max(chartData, (m: any) => m._areaStackTotal) || 100;
    } else if (barMode === 'stacked') {
      yMaxBar = d3.max(chartData, (m: any) => m._stackTotalAll || m._stackTotal);
    } else if (allBarKeys.length > 0) {
      yMaxBar = d3.max(chartData, (m: any) => {
        return d3.max(allBarKeys, (key: string) => m[key]);
      });
    } else {
      yMaxBar = 0;
    }

    // when lineOnPrimaryAxis, merge line/area max into the left axis
    if (lineOnPrimary) {
      const allPrimaryKeys = [...new Set([...allLineKeys, ...allAreaKeys])];
      const lineAreaMax: any = allPrimaryKeys.length > 0
        ? d3.max(chartData, (m: any) => d3.max(allPrimaryKeys, (key: string) => m[key]))
        : 0;
      yMaxBar = Math.max(yMaxBar || 0, lineAreaMax || 0);
    }
    if (!yMaxBar || yMaxBar <= 0) yMaxBar = 100;

    // yMax for right axis — use ALL keys so right axis stays stable on legend toggle
    let yMaxLine: any = 0;
    const allSecondaryKeys = (isStackedArea || lineOnPrimary) ? [] : [...new Set([...allLineKeys, ...allAreaKeys])];
    if (allSecondaryKeys.length > 0) {
      yMaxLine = d3.max(chartData, (m: any) => {
        return d3.max(allSecondaryKeys, (key: string) => m[key]);
      });
    }

    // dual axis only when there are secondary keys on right axis
    const hasDualAxis = allSecondaryKeys.length > 0 && allBarKeys.length > 0;

    let xExtent: any = d3.extent(chartData, (d: any) => d.date);
    const xMax = d3.max(chartData, (d: any) => d.date);
    xExtent[0] = timeConverter[dataPeriod].offset(xExtent[0], -1);
    xExtent[1] = timeConverter[dataPeriod].offset(xExtent[1], 1);

    const yMultiplier = isPercentAxis ? 1 : 1.1;
    const yMaxBarFull = Number(yMaxBar) * yMultiplier;
    const yMaxLineFull = Number(yMaxLine) * 1.2;

    // always sync slider ranges to actual data max on every data update
    // primary (left Y)
    if (isPercentAxis) {
      myChart.lineMin = 0;
      myChart.lineMax = 100;
    } else if (yMaxBarFull > 0) {
      myChart.lineMin = 0;
      myChart.lineMax = yMaxBarFull;
    } else {
      myChart.lineMin = 0;
      myChart.lineMax = 0;
    }
    // secondary (right Y)
    if (yMaxLineFull > 0) {
      myChart.lineMinRight = 0;
      myChart.lineMaxRight = yMaxLineFull;
    } else {
      myChart.lineMinRight = 0;
      myChart.lineMaxRight = 0;
    }

    // visible rows for brush
    let startingVisibleRows: any = parseInt(String(plotWidth / 25));
    startingVisibleRows = startingVisibleRows > chartData.length ? chartData.length : startingVisibleRows;
    const brushStartX = chartData.length === 0 ? 0 : (1 - (startingVisibleRows / chartData.length)) * plotWidth;

    // scales — primary yScale driven by lineMin/lineMax, secondary by lineMinRight/lineMaxRight
    const yScale = d3.scaleLinear().domain([myChart.lineMin, myChart.lineMax]).range([chartHeight, 0]);
    const yScaleBrush = d3.scaleLinear().domain([0, yMaxBarFull]).range([margins.brush, 0]);
    const yScaleRight = d3.scaleLinear().domain([myChart.lineMinRight, myChart.lineMaxRight]).range([chartHeight, 0]);
    const yScaleRightBrush = d3.scaleLinear().domain([0, yMaxLineFull]).range([margins.brush, 0]);
    const xScaleAll: any = d3.scaleTime().range([0, plotWidth]).domain(xExtent);
    const xScale: any = d3.scaleTime().range([0, plotWidth]).domain(xExtent);

    // brush
    const brush: any = d3.brushX()
      .handleSize(10)
      .extent([[0, 0], [plotWidth, margins.brush]])
      //@ts-ignore
      .on('start brush end', !myChart.props.showBrush ? null : brushed);

    // --- NO DATA MESSAGE ---
    mySvg.select('#noDataMessage' + myClass)
      .attr('x', margins.left + plotWidth / 2)
      .attr('y', margins.top + chartHeight / 2)
      .attr('font-family', 'Poppins')
      .attr('fill', '#101D42')
      .attr('font-size', '16px')
      .attr('font-weight', '500')
      .attr('text-anchor', 'middle')
      .text(chartData.length === 0 ? 'There is no data for this time period' : '');

    // --- INTERACTIVE LEGEND (click to toggle series) ---
    const legendGroup = mySvg.select('#legendGroup' + myClass);
    legendGroup.selectAll('*').remove();
    const legendMid = (margins.top / 2) - 20;
    const legendVis = chartData.length === 0 ? 'hidden' : 'visible';

    // Build all legend items in order: bars first, then areas, then lines
    const allLegendItems: any[] = [];
    allBarKeys.forEach((key: string) => allLegendItems.push({ key, colour: myChart.props.colours[key], type: 'rect' }));
    allAreaKeys.forEach((key: string) => allLegendItems.push({ key, colour: myChart.props.colours[key], type: 'area' }));
    allLineKeys.forEach((key: string) => {
      if (allAreaKeys.indexOf(key) === -1) allLegendItems.push({ key, colour: myChart.props.colours[key], type: 'line' });
    });

    // Measure each legend item
    const legendItemSpacing = 22;
    const iconGap = 6;
    const allLegendMeta: any[] = allLegendItems.map((item: any) => {
      const label = (myChart.props.legendLabels[item.key] || item.key).toUpperCase();
      const iconW = item.type === 'rect' ? 10 : item.type === 'area' ? 16 : 16;
      const textW = measureWidth(label, 12);
      return { ...item, label, iconW, textW, totalW: iconW + iconGap + textW };
    });
    const legendTotalWidth = allLegendMeta.reduce((sum: number, d: any) => sum + d.totalW + legendItemSpacing, 0);
    const arrowSpace = 30;
    const legendFits = legendTotalWidth <= width;
    const visibleAreaLeft = legendFits ? 0 : arrowSpace;
    const visibleAreaWidth = legendFits ? width : width - arrowSpace * 2;

    // Clip-path for legend scrolling area
    const legendClipId = 'legendClip' + myClass;
    let legendClip = mySvg.select('defs').select('#' + legendClipId);
    if (legendClip.empty()) {
      legendClip = mySvg.select('defs').append('clipPath').attr('id', legendClipId);
      legendClip.append('rect');
    }
    legendClip.select('rect')
      .attr('x', visibleAreaLeft).attr('y', legendMid - 15)
      .attr('width', visibleAreaWidth).attr('height', 30);

    // Scrollable inner group
    const legendInner = legendGroup.append('g')
      .attr('class', 'legendInner')
      .attr('clip-path', legendFits ? null : 'url(#' + legendClipId + ')');

    const legendContent = legendInner.append('g').attr('class', 'legendContent');

    // Render ALL legend items inside legendContent
    let legendX = legendFits ? (width - legendTotalWidth) / 2 : arrowSpace;
    allLegendMeta.forEach((item: any) => {
      const isHidden = myChart.hiddenKeys.has(item.key);
      const itemG = legendContent.append('g')
        .attr('class', 'legendItemGroup')
        .style('cursor', 'pointer')
        .style('opacity', isHidden ? 0.3 : 1)
        .on('click', () => {
          if (myChart.hiddenKeys.has(item.key)) {
            myChart.hiddenKeys.delete(item.key);
          } else {
            myChart.hiddenKeys.add(item.key);
          }
          myChart.lineMin = 0;
          myChart.lineMax = 0;
          myChart.lineMinRight = 0;
          myChart.lineMaxRight = 0;
          d3.select('#svg_' + myClass).remove();
          myChart.initiateCharts();
          myChart.plotChart();
        });

      if (item.type === 'rect') {
        itemG.append('rect')
          .attr('x', legendX).attr('y', legendMid - 5)
          .attr('width', 6).attr('height', 10)
          .attr('rx', 1.5).attr('fill', item.colour)
          .attr('visibility', legendVis);
        legendX += 6 + iconGap;
      } else if (item.type === 'area') {
        const iW = 12, iH = 8;
        itemG.append('path')
          .attr('d', 'M' + legendX + ',' + (legendMid + iH / 2) +
            ' Q' + (legendX + iW * 0.25) + ',' + (legendMid - iH / 2 - 1.5) +
            ' ' + (legendX + iW * 0.5) + ',' + (legendMid - iH / 2) +
            ' Q' + (legendX + iW * 0.75) + ',' + (legendMid - iH / 2 - 2) +
            ' ' + (legendX + iW) + ',' + (legendMid + iH / 2) + ' Z')
          .attr('fill', item.colour).attr('opacity', 0.4)
          .attr('stroke', item.colour).attr('stroke-width', 1.2)
          .attr('visibility', legendVis);
        legendX += iW + iconGap;
      } else {
        itemG.append('line')
          .attr('x1', legendX).attr('y1', legendMid)
          .attr('x2', legendX + 10).attr('y2', legendMid)
          .attr('stroke', item.colour).attr('stroke-width', 2)
          .attr('visibility', legendVis);
        itemG.append('circle')
          .attr('cx', legendX + 5).attr('cy', legendMid)
          .attr('r', 2.5).attr('fill', item.colour)
          .attr('visibility', legendVis);
        legendX += 10 + iconGap;
      }
      itemG.append('text')
        .attr('x', legendX).attr('y', legendMid + 5)
        .attr('font-family', 'Poppins')
        .attr('fill', isHidden ? '#B0B0B0' : '#101D42')
        .attr('font-size', '12px').attr('font-weight', '500')
        .attr('visibility', legendVis)
        .text(item.label?.toUpperCase());
      legendX += item.textW + legendItemSpacing;
    });

    // Smooth scroll arrows (only when legend overflows)
    if (!legendFits) {
      let scrollOffset = 0;
      const scrollStep = 80;
      const maxScroll = Math.max(0, legendTotalWidth - visibleAreaWidth);

      function updateArrowVisibility(): void {
        arrowLeftG.attr('visibility', scrollOffset > 0 ? 'visible' : 'hidden');
        arrowRightG.attr('visibility', scrollOffset < maxScroll ? 'visible' : 'hidden');
      }

      // Left arrow group
      const arrowLeftG = legendGroup.append('g')
        .attr('class', 'legendArrowLeft')
        .style('cursor', 'pointer')
        .attr('visibility', 'hidden')
        .on('click', () => {
          scrollOffset = Math.max(0, scrollOffset - scrollStep);
          legendContent.transition().duration(300).ease(d3.easeCubicOut)
            .attr('transform', 'translate(' + (-scrollOffset) + ',0)');
          updateArrowVisibility();
        });
      const arrowR = 8;
      const arrowLeftCx = 10;
      const arrowRightCx = width - 10;
      arrowLeftG.append('circle')
        .attr('r', arrowR).attr('fill', '#F5F7FA').attr('stroke', '#E0E4EA')
        .attr('stroke-width', 1).attr('cx', arrowLeftCx).attr('cy', legendMid - 1);
      arrowLeftG.append('path')
        .attr('d', 'M' + (arrowLeftCx + 2) + ',' + (legendMid - 4) + ' L' + (arrowLeftCx - 2.5) + ',' + (legendMid - 1) + ' L' + (arrowLeftCx + 2) + ',' + (legendMid + 2))
        .attr('fill', 'none').attr('stroke', '#1363DF').attr('stroke-width', 1.5)
        .attr('stroke-linecap', 'round').attr('stroke-linejoin', 'round');

      // Right arrow group
      const arrowRightG = legendGroup.append('g')
        .attr('class', 'legendArrowRight')
        .style('cursor', 'pointer')
        .attr('visibility', maxScroll > 0 ? 'visible' : 'hidden')
        .on('click', () => {
          scrollOffset = Math.min(maxScroll, scrollOffset + scrollStep);
          legendContent.transition().duration(300).ease(d3.easeCubicOut)
            .attr('transform', 'translate(' + (-scrollOffset) + ',0)');
          updateArrowVisibility();
        });
      arrowRightG.append('circle')
        .attr('r', arrowR).attr('fill', '#F5F7FA').attr('stroke', '#E0E4EA')
        .attr('stroke-width', 1).attr('cx', arrowRightCx).attr('cy', legendMid - 1);
      arrowRightG.append('path')
        .attr('d', 'M' + (arrowRightCx - 2) + ',' + (legendMid - 4) + ' L' + (arrowRightCx + 2.5) + ',' + (legendMid - 1) + ' L' + (arrowRightCx - 2) + ',' + (legendMid + 2))
        .attr('fill', 'none').attr('stroke', '#1363DF').attr('stroke-width', 1.5)
        .attr('stroke-linecap', 'round').attr('stroke-linejoin', 'round');
    }

    // --- CLIP RECT (scoped to chart area only, not full SVG height) ---
    mySvg.select('.overlay').style('fill', 'transparent');
    mySvg.select('#brushClipRect' + myClass)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', plotWidth)
      .attr('height', chartHeight)
      .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')');

    // --- AXIS LABELS (positioned above axes, colored to match reference) ---
    mySvg.select('#yAxisLabel' + myClass)
      .attr('visibility', chartData.length === 0 ? 'hidden' : 'visible')
      .attr('x', margins.left)
      .attr('y', margins.top - 14)
      .attr('text-anchor', 'start')
      .attr('font-family', 'Poppins')
      .attr('fill', myChart.props.yAxisLabelColour || '#737D88')
      .attr('font-size', '12px')
      .attr('font-weight', '400')
      .attr('transform', '')
      .text(myChart.props.yAxisLabel?.toUpperCase() || '');

    mySvg.select('#yAxisRightLabel' + myClass)
      .attr('visibility', chartData.length === 0 || !hasDualAxis ? 'hidden' : 'visible')
      .attr('x', margins.left + plotWidth)
      .attr('y', margins.top - 14)
      .attr('text-anchor', 'end')
      .attr('font-family', 'Poppins')
      .attr('fill', myChart.props.yAxisRightLabelColour || '#737D88')
      .attr('font-size', '12px')
      .attr('font-weight', '400')
      .attr('transform', '')
      .text(myChart.props.yAxisRightLabel?.toUpperCase() || '');

    mySvg.select('#xAxisLabel' + myClass)
      .attr('visibility', chartData.length === 0 ? 'hidden' : 'visible')
      .attr('x', margins.left + plotWidth / 2)
      .attr('y', height - margins.bottom - margins.brush - 10)
      .attr('text-anchor', 'middle')
      .attr('font-family', 'Poppins')
      .attr('fill', '#737D88')
      .attr('font-size', '12px')
      .text(myChart.props.xAxisLabel?.toUpperCase() || '');

    // --- AXES ---
    mySvg.select('#xAxis' + myClass)
      .call(d3.axisBottom(xScale).tickSizeOuter(0).tickValues(tickValues).ticks(tickCount).tickFormat(xTickFormat))
      .attr('transform', 'translate(' + margins.left + ',' + (margins.top + chartHeight) + ')');

    mySvg.select('#yAxis' + myClass)
      //@ts-ignore
      .call(d3.axisLeft(yScale).tickSizeOuter(0)
        .ticks(isPercentAxis ? 6 : 7)
        .tickValues(isPercentAxis ? [0, 20, 40, 60, 80, 100].filter((v: number) => { const dom = yScale.domain(); return v >= dom[0] && v <= dom[1]; }) as any : null as any)
        .tickFormat((d: any) => {
          if (isPercentAxis) {
            const fmt = myChart.props.yAxisFormatting || '.0f';
            return d3.format(fmt)(d) + '%';
          }
          return d3.format(myChart.props.yAxisFormatting)(d) + (myChart.props.yAxisSuffix || '');
        }))
      .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')');

    if (hasDualAxis) {
      mySvg.select('#yAxisRight' + myClass)
        .attr('visibility', 'visible')
        .call(d3.axisRight(yScaleRight).tickSizeOuter(0).ticks(7).tickFormat((d: any) => d3.format(myChart.props.yAxisRightFormatting || '-2~s')(d) + (myChart.props.yAxisRightSuffix || '')))
        .attr('transform', 'translate(' + (margins.left + plotWidth) + ',' + margins.top + ')');
    } else {
      mySvg.select('#yAxisRight' + myClass).attr('visibility', 'hidden');
    }

    mySvg.selectAll('.axis' + myClass + ' path')
      .attr('visibility', chartData.length === 0 ? 'hidden' : 'visible')
      .style('stroke', '#E8EAEE');

    // --- LINE/AREA GENERATORS ---
    const areaGenerators: any = {};
    const areaGeneratorsBrush: any = {};
    areaKeys.forEach((key: string) => {
      if (isStackedArea) {
        // stacked area: use left yScale with cumulative y0/y1
        areaGenerators[key] = d3.area()
          .curve(d3.curveMonotoneX)
          .x((d: any) => xScale(d.date))
          .y0((d: any) => yScale(d[key + '_y0']))
          .y1((d: any) => yScale(d[key + '_y1']));
        areaGeneratorsBrush[key] = d3.area()
          .curve(d3.curveMonotoneX)
          .x((d: any) => xScaleAll(d.date))
          .y0((d: any) => yScaleBrush(d[key + '_y0']))
          .y1((d: any) => yScaleBrush(d[key + '_y1']));
      } else {
        // normal area: use primary or secondary Y-axis
        const yS = lineOnPrimary ? yScale : yScaleRight;
        const ySB = lineOnPrimary ? yScaleBrush : yScaleRightBrush;
        areaGenerators[key] = d3.area()
          .curve(d3.curveMonotoneX)
          .x((d: any) => xScale(d.date))
          .y0(chartHeight)
          .y1((d: any) => yS(d[key]));
        areaGeneratorsBrush[key] = d3.area()
          .curve(d3.curveMonotoneX)
          .x((d: any) => xScaleAll(d.date))
          .y0(margins.brush)
          .y1((d: any) => ySB(d[key]));
      }
    });
    // line generators
    const lineGenerators: any = {};
    const lineGeneratorsBrush: any = {};
    // for stacked area: stroke lines on top edge
    if (isStackedArea) {
      areaKeys.forEach((key: string) => {
        lineGenerators[key] = d3.line()
          .curve(d3.curveMonotoneX)
          .x((d: any) => xScale(d.date))
          .y((d: any) => yScale(d[key + '_y1']));
        lineGeneratorsBrush[key] = d3.line()
          .curve(d3.curveMonotoneX)
          .x((d: any) => xScaleAll(d.date))
          .y((d: any) => yScaleBrush(d[key + '_y1']));
      });
    }
    // line generators for secondary keys (right axis) or primary keys (lineOnPrimary)
    const lineScaleKeys = lineOnPrimary ? [...new Set([...lineKeys, ...areaKeys])] : allSecondaryKeys;
    const yLineScale = lineOnPrimary ? yScale : yScaleRight;
    const yLineScaleBrush = lineOnPrimary ? yScaleBrush : yScaleRightBrush;
    lineScaleKeys.forEach((key: string) => {
      if (!lineGenerators[key]) {  // don't overwrite stacked area stroke generators
        lineGenerators[key] = d3.line()
          .curve(d3.curveMonotoneX)
          .defined((d: any) => d[key] !== null && d[key] !== undefined)
          .x((d: any) => xScale(d.date))
          .y((d: any) => yLineScale(d[key]));
        lineGeneratorsBrush[key] = d3.line()
          .curve(d3.curveMonotoneX)
          .defined((d: any) => d[key] !== null && d[key] !== undefined)
          .x((d: any) => xScaleAll(d.date))
          .y((d: any) => yLineScaleBrush(d[key]));
      }
    });

    // --- CHART GROUP DATA ---
    const chartGroupData = myChart.props.showBrush === false ? ['chart'] : ['brush', 'chart'];
    let isInitialRender = false;
    let brushReady = false;

    // --- BRUSH SETUP ---
    mySvg.select('#brushGroup' + myClass)
      .attr('transform', 'translate(' + margins.left + ',' + (margins.top + chartHeight + margins.mid) + ')')
      .call(brush)
      .call(brush.move, [brushStartX, plotWidth]);

    if (myChart.props.showBrush === false) {
      mySvg.select('#brushGroup' + myClass)
        .call(brush.move, null)
        .selectAll('*').remove();
    }

    mySvg.selectAll('.handleLines' + myClass)
      .attr('visibility', chartData.length === 0 || myChart.props.showBrush === false ? 'hidden' : 'visible')
      .attr('y1', margins.top + chartHeight + margins.mid + (margins.brush - 12) / 2)
      .attr('y2', margins.top + chartHeight + margins.mid + 12 + (margins.brush - 12) / 2)
      .style('stroke', '#8A98AB')
      .style('stroke-width', '1')
      .attr('transform', 'translate(' + margins.left + ',0)');

    mySvg.selectAll('#brushGroup' + myClass)
      .selectAll('.selection')
      .attr('fill', '#A0A0A0')
      .attr('fill-opacity', '0.2')
      .style('stroke', '#E8EAEE')
      .style('stroke-width', '1');

    // allow brush events from now on
    brushReady = true;

    // single drawChart call with animation — this is the ONLY render
    isInitialRender = true;
    drawChart();
    isInitialRender = false;

    // =========================================================
    // D3 Range Sliders — primary (left) and secondary (right)
    // =========================================================
    const slider = mySvg.select('#sliderGroup' + myClass);
    if (myChart.props.slider) {
      // Primary slider on left Y-axis
      createRangeSlider('primary', 0, yMaxBarFull, 'left', yScale, '#yAxis' + myClass,
        () => myChart.lineMin, (v: number) => myChart.lineMin = v,
        () => myChart.lineMax, (v: number) => myChart.lineMax = v,
        '#e8eaee');
      // Secondary slider on right Y-axis (only if dual axis)
      if (hasDualAxis) {
        createRangeSlider('secondary', 0, yMaxLineFull, 'right', yScaleRight, '#yAxisRight' + myClass,
          () => myChart.lineMinRight, (v: number) => myChart.lineMinRight = v,
          () => myChart.lineMaxRight, (v: number) => myChart.lineMaxRight = v,
          '#e8eaee');
      }
    }

    // =========================================================
    // drawChart() — called on init and on every brush event
    // =========================================================
    function drawChart(): void {
      // axis styling
      mySvg.selectAll('#xAxis' + myClass + ' line')
        .attr('y1', '0')
        .attr('y2', -chartHeight)
        .style('stroke', '#F0F3F6')
        .style('stroke-width', 1);

      mySvg.selectAll('.axis' + myClass + ' text')
        .style('font-weight', myChart.props.axisFontWeight)
        .style('font-family', 'Poppins')
        .style('font-size', myChart.props.axisFontSize);

      mySvg.selectAll('#xAxis' + myClass + ' text')
        .style('text-anchor', 'middle')
        .attr('dx', -5)
        .attr('dy', 5)
        .attr('transform', '');

      mySvg.selectAll('#yAxis' + myClass + ' line')
        .attr('x1', '0')
        .attr('x2', plotWidth)
        .style('stroke', '#F0F3F6')
        .style('stroke-width', 1);

      if (hasDualAxis) {
        mySvg.selectAll('#yAxisRight' + myClass + ' line').style('stroke', 'transparent');
      }

      // tick overlap detection
      const xBandwidth = dateGroupDates.length > 1 ? xScale(dateGroupDates[1]) - xScale(dateGroupDates[0]) : 0;
      const maxTextWidth = getMaxTextWidth();
      if (maxTextWidth > (xBandwidth - 5)) {
        let myFontSize = xBandwidth < myChart.props.axisFontSize + 2 ? xBandwidth - 2 : myChart.props.axisFontSize;
        mySvg.selectAll('#xAxis' + myClass + ' text')
          .style('text-anchor', (myChart.props.d3AxisFormatting === true ? 'middle' : 'end'))
          .attr('dy', (myChart.props.d3AxisFormatting === true ? 5 : 1))
          .style('font-size', myFontSize)
          .style('font-family', 'Poppins')
          .attr('transform', 'rotate(' + (myChart.props.d3AxisFormatting === true ? 0 : -45) + ')');
      }

      // brush handle lines
      if (myChart.props.showBrush === true) {
        const handleEastX = +mySvg.select('#brushGroup' + myClass).select('.handle--e').attr('x');
        const handleWestX = +mySvg.select('#brushGroup' + myClass).select('.handle--w').attr('x');
        mySvg.select('.handleLeftLine1' + myClass).attr('x1', handleEastX + 3).attr('x2', handleEastX + 3);
        mySvg.select('.handleLeftLine2' + myClass).attr('x1', handleEastX + 7).attr('x2', handleEastX + 7);
        mySvg.select('.handleRightLine1' + myClass).attr('x1', handleWestX + 3).attr('x2', handleWestX + 3);
        mySvg.select('.handleRightLine2' + myClass).attr('x1', handleWestX + 7).attr('x2', handleWestX + 7);
        mySvg.select('#brushGroup' + myClass)
          .selectAll('.handle')
          .attr('fill', 'white')
          .attr('rx', 4).attr('ry', 4)
          .attr('y', (margins.brush - 24) / 2)
          .attr('height', 24)
          .style('filter', 'url(#drop-shadow-' + myClass + ')');
      }

      // partial period line
      mySvg.select('.partialPeriodLine' + myClass)
        .attr('visibility', myChart.props.partialPeriod === true ? 'visible' : 'hidden')
        .attr('x1', margins.left + xScale(xMax))
        .attr('x2', margins.left + xScale(xMax))
        .attr('y1', margins.top)
        .attr('y2', chartHeight + margins.top)
        .attr('stroke', '#1363DF')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '2,2');

      mySvg.select('.partialPeriodLineLabel' + myClass)
        .attr('visibility', myChart.props.partialPeriod === true ? 'visible' : 'hidden')
        .attr('transform', 'translate(' + (margins.left + xScale(xMax)) + ',' + (margins.top - 12) + ')')
        .attr('text-anchor', 'middle')
        .attr('dy', 0)
        .attr('font-size', '8px')
        .attr('font-family', 'Poppins')
        .attr('font-weight', '400')
        .attr('fill', '#1363DF')
        .text('Partial period')
        .call(wrap, 35);

      mySvg.selectAll('.selection')
        .attr('visibility', chartData.length === 0 ? 'hidden' : 'visible');

      myChart.currentVisibleData = [chartData, xScale.domain()];

      // Visible-data slice for the MAIN chart line/area paths.
      // The smoothed line generators use xScale (the brushed scale); passing
      // the full chartData lets off-screen points distort the visible curve
      // at the brush edges. Slice strictly to the visible domain — no buffer
      // points, because curveCardinal uses neighbors for tangents and a
      // buffer point would still pull the visible endpoint toward it.
      const visibleChartData: any = (() => {
        const [d0, d1] = xScale.domain();
        return chartData.filter((d: any) => d.date >= d0 && d.date <= d1);
      })();

      // ---- MAIN CHART GROUP (dual: brush + chart) ----
      // Z-order: area (back) → bars → line stroke → dots (front)
      const chartGroup = mySvg.select('#chartGroup' + myClass)
        .selectAll('.chartGroup' + myClass)
        .data(chartGroupData)
        .join((group: any) => {
          const enter = group.append('g').attr('class', 'chartGroup' + myClass);
          // 1. area fills (behind everything)
          areaKeys.forEach((key: string) => {
            enter.append('path').attr('class', 'areaPath areaPath_' + key);
          });
          // 2. bars group (on top of area)
          enter.append('g').attr('class', 'barsGroup');
          // 3. area stroke line (on top of bars for visibility)
          areaKeys.forEach((key: string) => {
            enter.append('path').attr('class', 'areaStrokePath areaStrokePath_' + key);
          });
          // 4. line paths (non-area lines — from either secondary or primary axis)
          lineScaleKeys.forEach((key: string) => {
            if (areaKeys.indexOf(key) === -1) {
              enter.append('path').attr('class', 'linePath linePath_' + key);
            }
          });
          // 5. line dots (front)
          lineScaleKeys.forEach((key: string) => {
            enter.append('g').attr('class', 'lineDotsGroup lineDotsGroup_' + key);
          });
          // 6. data labels (topmost)
          enter.append('g').attr('class', 'barLabelsGroup');
          lineScaleKeys.forEach((key: string) => {
            enter.append('g').attr('class', 'lineLabelsGroup lineLabelsGroup_' + key);
          });
          return enter;
        });

      chartGroup.attr('clip-path', (d: any) => d === 'brush' ? '' : 'url(#brushClip' + myClass + ')');

      // ---- AREA FILL (solid colour) with left-to-right reveal ----
      areaKeys.forEach((key: string, ki: number) => {
        const hidden = myChart.hiddenKeys.has(key);
        const targetOpacity = hidden ? 0 : (isStackedArea ? 1 : 1);
        chartGroup.select('.areaPath_' + key)
          .attr('fill', myChart.props.colours[key])
          .attr('stroke', 'none')
          .attr('opacity', targetOpacity)
          // Area paths never catch pointer events — the hover overlay rect
          // (see "UNIVERSAL HOVER OVERLAY" below) handles hover for areas.
          // This is required in crosshairMode='hover' so that, after the
          // overlay is lowered beneath bars/dots, mousemove over an area fill
          // still reaches the overlay instead of being swallowed by the area.
          .style('pointer-events', 'none')
          .attr('d', (d: any) => d === 'brush' ? areaGeneratorsBrush[key](chartData) : areaGenerators[key](visibleChartData))
          .attr('transform', (d: any) => 'translate(' + margins.left + ',' + (d === 'brush' ? (margins.mid + chartHeight + margins.top) : margins.top) + ')')
          .each(function (this: any, d: any) {
            if (isInitialRender && myChart.props.animate && d !== 'brush' && !hidden) {
              const el = d3.select(this);
              // use stroke-dasharray trick on the area path for a wipe-in effect
              const totalLength = (el.node() as SVGPathElement).getTotalLength();
              el.attr('stroke-dasharray', totalLength)
                .attr('stroke-dashoffset', totalLength)
                .attr('stroke', myChart.props.colours[key])
                .attr('stroke-width', 1)
                .attr('opacity', 0)
                .transition().duration(800).delay(ki * 100).ease(d3.easeQuadOut)
                .attr('stroke-dashoffset', 0)
                .attr('opacity', targetOpacity)
                .on('end', function () {
                  d3.select(this).attr('stroke', 'none').attr('stroke-dasharray', null).attr('stroke-dashoffset', null);
                });
            }
          });
      });

      // ---- AREA STROKE LINE (only for stacked area mode — white separator between layers) ----
      areaKeys.forEach((key: string) => {
        if (isStackedArea && lineGenerators[key]) {
          chartGroup.select('.areaStrokePath_' + key)
            .style('stroke', 'white')
            .style('fill', 'transparent')
            .style('stroke-width', 1.5)
            .style('pointer-events', 'none')
            .attr('d', (d: any) => d === 'brush' ? lineGeneratorsBrush[key](chartData) : lineGenerators[key](visibleChartData))
            .attr('transform', (d: any) => 'translate(' + margins.left + ',' + (d === 'brush' ? (margins.mid + chartHeight + margins.top) : margins.top) + ')');
        } else {
          // hide stroke for non-stacked-area — area is purely a background fill
          chartGroup.select('.areaStrokePath_' + key)
            .style('stroke', 'none')
            .style('fill', 'transparent');
        }
      });

      // ---- LINE RENDERING (non-area lines) with draw-on animation ----
      lineScaleKeys.forEach((key: string) => {
        if (areaKeys.indexOf(key) === -1) {
          const hidden = myChart.hiddenKeys.has(key);
          chartGroup.select('.linePath_' + key)
            .style('stroke', myChart.props.colours[key])
            .style('fill', 'transparent')
            .style('stroke-width', 3)
            .style('opacity', hidden ? 0 : 1)
            .attr('d', (d: any) => d === 'brush' ? lineGeneratorsBrush[key](chartData) : lineGenerators[key](visibleChartData))
            .attr('transform', (d: any) => 'translate(' + margins.left + ',' + (d === 'brush' ? (margins.mid + chartHeight + margins.top) : margins.top) + ')')
            .each(function (this: any, d: any) {
              if (isInitialRender && myChart.props.animate && d !== 'brush' && !hidden) {
                const path = d3.select(this);
                const totalLength = (path.node() as SVGPathElement).getTotalLength();
                path.attr('stroke-dasharray', totalLength)
                  .attr('stroke-dashoffset', totalLength)
                  .transition().duration(800).ease(d3.easeQuadOut)
                  .attr('stroke-dashoffset', 0)
                  .on('end', function () {
                    d3.select(this).attr('stroke-dasharray', null).attr('stroke-dashoffset', null);
                  });
              } else {
                d3.select(this).interrupt().attr('stroke-dasharray', null).attr('stroke-dashoffset', null);
              }
            });
        }
      });

      // ---- LINE DOTS (only for non-area, non-hidden line keys) ----
      lineScaleKeys.forEach((key: string) => {
        const dotsGroup = chartGroup.select('.lineDotsGroup_' + key);
        // skip dots for area keys, hidden keys, or when dots are disabled
        if (!dotShow || areaKeys.indexOf(key) > -1 || myChart.hiddenKeys.has(key)) {
          dotsGroup.selectAll('*').remove();
          return;
        }
        dotsGroup.attr('transform', (d: any) => 'translate(0,' + (d === 'brush' ? (margins.mid + chartHeight) : 0) + ')');

        const dots = dotsGroup.selectAll('.lineDot_' + key)
          .data((d: any) => {
            const myData = JSON.parse(JSON.stringify(chartData));
            myData.map((n: any) => { n.date = new Date(n.date); n.type = d; });
            return myData.filter((f: any) => f[key] !== null && f[key] !== undefined);
          })
          .join((enter: any) => {
            return enter.append('circle').attr('class', 'lineDot_' + key);
          });

        dots
          .attr('cx', (d: any) => d.type === 'brush' ? xScaleAll(d.date) : xScale(d.date))
          .attr('cy', (d: any) => d.type === 'brush' ? yLineScaleBrush(d[key]) : yLineScale(d[key]))
          .attr('fill', dotFill)
          .style('stroke', myChart.props.colours[key])
          .style('stroke-width', dotStrokeWidth)
          .each(function (this: any, d: any) {
            const dot = d3.select(this);
            const targetR = d.type === 'brush' ? 2 : circleRadius;
            if (isInitialRender && myChart.props.animate && d.type !== 'brush') {
              dot.attr('r', 0).transition().duration(400).delay(600).attr('r', targetR);
            } else {
              dot.attr('r', targetR);
            }
          })
          .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')');
      });

      // ---- BARS RENDERING ----
      chartGroup.select('.barsGroup')
        .attr('transform', (d: any) => 'translate(0,' + (d === 'brush' ? (margins.mid + chartHeight) : 0) + ')');

      const barGroup = chartGroup.select('.barsGroup')
        .selectAll('.barDateGroup')
        .data((d: any) => {
          const myData = JSON.parse(JSON.stringify(chartData));
          myData.map((n: any) => { n.date = new Date(n.date); n.type = d; });
          return myData;
        })
        .join((group: any) => {
          const enter = group.append('g').attr('class', 'barDateGroup');
          barKeys.forEach((key: string) => {
            enter.append('path').attr('class', 'barRect barRect_' + key);
          });
          return enter;
        });

      // calculate bar widths
      const barPadding = 2;
      const numBarsPerGroup = barMode === 'clustered' ? barKeys.length : 1;

      barKeys.forEach((key: string, ki: number) => {
        const barHidden = myChart.hiddenKeys.has(key);
        barGroup.select('.barRect_' + key).each(function (this: any, d: any) {
          const el = d3.select(this);
          if (barHidden) { el.attr('d', '').attr('opacity', 0); return; }
          const myXScale = d.type === 'brush' ? xScaleAll : xScale;
          const myYScale = d.type === 'brush' ? yScaleBrush : yScale;
          const bw = dateGroupDates.length > 1
            ? Math.abs(myXScale(dateGroupDates[1]) - myXScale(dateGroupDates[0]))
            : plotWidth / 2;
          const maxBarWidth = Math.max(2, (bw * 0.7) / numBarsPerGroup - barPadding);

          // barRadius props: { value: 4, mode: 'all' | 'top' | 'none' | 'eachTop' }
          const brProps = myChart.props.barRadius || { value: 4, mode: 'eachTop' };
          const brMode = brProps.mode || 'all';
          const brValue = brProps.value != null ? brProps.value : 4;
          const isTopBar = ki === barKeys.length - 1;

          // helper: build SVG path for a rect with per-corner radii
          // always uses same path structure (with arcs) so d3 can interpolate smoothly
          const rrPath = (bx: number, by: number, bw2: number, bh2: number, rtl: number, rtr: number, rbr2: number, rbl: number) => {
            if (bw2 <= 0) bw2 = 0;
            if (bh2 <= 0) bh2 = 0;
            rtl = bh2 > 0 ? Math.min(rtl, bw2 / 2, bh2 / 2) : 0;
            rtr = bh2 > 0 ? Math.min(rtr, bw2 / 2, bh2 / 2) : 0;
            rbr2 = bh2 > 0 ? Math.min(rbr2, bw2 / 2, bh2 / 2) : 0;
            rbl = bh2 > 0 ? Math.min(rbl, bw2 / 2, bh2 / 2) : 0;
            return `M${bx + rtl},${by}`
              + `H${bx + bw2 - rtr}`
              + `A${rtr},${rtr},0,0,1,${bx + bw2},${by + rtr}`
              + `V${by + bh2 - rbr2}`
              + `A${rbr2},${rbr2},0,0,1,${bx + bw2 - rbr2},${by + bh2}`
              + `H${bx + rbl}`
              + `A${rbl},${rbl},0,0,1,${bx},${by + bh2 - rbl}`
              + `V${by + rtl}`
              + `A${rtl},${rtl},0,0,1,${bx + rtl},${by}`
              + 'Z';
          };

          // resolve per-corner radii based on mode and bar type
          const isStacked = barMode !== 'clustered';
          const resolveRadii = (r: number, mode: string, top: boolean) => {
            if (mode === 'none' || r <= 0) return [0, 0, 0, 0];
            if (mode === 'all') return [r, r, r, r];
            if (mode === 'top' && top) return [r, r, 0, 0];
            if (mode === 'top' && !top) return [0, 0, 0, 0];
            // eachTop: clustered = every bar gets top radius; stacked = only topmost segment
            if (mode === 'eachTop') return (isStacked && !top) ? [0, 0, 0, 0] : [r, r, 0, 0];
            return [0, 0, 0, 0];
          };

          if (barMode === 'clustered') {
            const groupWidth = maxBarWidth * numBarsPerGroup + barPadding * (numBarsPerGroup - 1);
            const barX = myXScale(d.date) - groupWidth / 2 + ki * (maxBarWidth + barPadding);
            const barY = myYScale(d[key]);
            const barH = Math.max(0, myYScale(0) - myYScale(d[key]));
            const rMax = d.type === 'brush' ? Math.min(2, brValue) : brValue;
            const r = Math.min(rMax, maxBarWidth / 2, barH / 2);
            const [rtl, rtr, rbr, rbl] = resolveRadii(r, brMode, isTopBar);
            el.attr('fill', myChart.props.colours[key])
              .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')');
            if (isInitialRender && myChart.props.animate && d.type !== 'brush') {
              el.attr('d', rrPath(barX, myYScale(0), maxBarWidth, 0, rtl, rtr, rbr, rbl))
                .transition().duration(600).delay(ki * 60 + 100).ease(d3.easeCubicOut)
                .attr('d', rrPath(barX, barY, maxBarWidth, barH, rtl, rtr, rbr, rbl));
            } else {
              el.attr('d', rrPath(barX, barY, maxBarWidth, barH, rtl, rtr, rbr, rbl));
            }
          } else {
            const stackBarWidth = Math.max(2, bw * 0.45);
            const barX = myXScale(d.date) - stackBarWidth / 2;
            let barY = myYScale(d[key + '_y1']);
            let barH = Math.max(0, myYScale(d[key + '_y0']) - myYScale(d[key + '_y1']));
            const rMax = d.type === 'brush' ? Math.min(2, brValue) : brValue;
            const r = Math.min(rMax, stackBarWidth / 2, barH / 2);
            const [rtl, rtr, rbr, rbl] = resolveRadii(r, brMode, isTopBar);
            el.attr('fill', myChart.props.colours[key])
              .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')');
            if (isInitialRender && myChart.props.animate && d.type !== 'brush') {
              el.attr('d', rrPath(barX, myYScale(d[key + '_y0']), stackBarWidth, 0, rtl, rtr, rbr, rbl))
                .transition().duration(600).delay(ki * 80 + 100).ease(d3.easeCubicOut)
                .attr('d', rrPath(barX, barY, stackBarWidth, barH, rtl, rtr, rbr, rbl));
            } else {
              el.attr('d', rrPath(barX, barY, stackBarWidth, barH, rtl, rtr, rbr, rbl));
            }
          }
        });
      });

      // ---- BAR DATA LABELS ----
      const barLabelsGroup = chartGroup.select('.barLabelsGroup');
      barLabelsGroup
        .attr('transform', (d: any) => 'translate(0,' + (d === 'brush' ? (margins.mid + chartHeight) : 0) + ')');
      // remove old labels from both barLabelsGroup and barGroup
      barLabelsGroup.selectAll('.barLabel').remove();
      barGroup.selectAll('.barLabel').remove();
      if (dlShowBar && barKeys.length > 0) {
        barGroup.each(function (this: any, d: any) {
          if (d.type === 'brush') return;
          const parent = d3.select(this);
          const myXScale = xScale;
          const myYScale = yScale;
          const bw = dateGroupDates.length > 1
            ? Math.abs(myXScale(dateGroupDates[1]) - myXScale(dateGroupDates[0]))
            : plotWidth / 2;

          if (barMode === 'clustered') {
            const maxBarWidth = Math.max(2, (bw * 0.7) / numBarsPerGroup - barPadding);
            const groupWidth = maxBarWidth * numBarsPerGroup + barPadding * (numBarsPerGroup - 1);
            let clusterTotal = 0;
            let clusterTopY = Infinity;
            // each bar's value centered on the bar
            barKeys.forEach((key: string, ki: number) => {
              if (myChart.hiddenKeys.has(key) || d[key] === null || d[key] === undefined) return;
              const barX = myXScale(d.date) - groupWidth / 2 + ki * (maxBarWidth + barPadding) + maxBarWidth / 2;
              const barY = myYScale(d[key]);
              const barBottom = myYScale(0);
              const barH = barBottom - barY;
              clusterTotal += d[key];
              if (barY < clusterTopY) clusterTopY = barY;
              if (dlShowValue && barH >= dlFontSize + 2) {
                const barCenterY = barY + barH / 2;
                parent.append('text').attr('class', 'barLabel')
                  .attr('x', barX + margins.left)
                  .attr('y', barCenterY + margins.top + dlFontSize / 3)
                  .attr('text-anchor', 'middle')
                  .attr('fill', dlColor)
                  .attr('font-family', 'Poppins')
                  .attr('font-size', dlFontSize + 'px')
                  .attr('font-weight', dlFontWeight)
                  .text(d3.format(dlFormat)(d[key]));
              }
            });
            // total on top
            if (dlShowTotal && clusterTotal > 0) {
              parent.append('text').attr('class', 'barLabel')
                .attr('x', myXScale(d.date) + margins.left)
                .attr('y', clusterTopY + margins.top - 5)
                .attr('text-anchor', 'middle')
                .attr('fill', dlTotalColor)
                .attr('font-family', 'Poppins')
                .attr('font-size', dlFontSize + 'px')
                .attr('font-weight', dlTotalFontWeight)
                .text(d3.format(dlFormat)(clusterTotal));
            }
          } else {
            // stacked: each bar's value centered within its segment
            let stackTotal = 0;
            let stackTopY = Infinity;
            barKeys.forEach((key: string) => {
              if (myChart.hiddenKeys.has(key) || d[key + '_y1'] == null || d[key] === null || d[key] === undefined || d[key] === 0) return;
              const barX = myXScale(d.date);
              const barTop = myYScale(d[key + '_y1']);
              const barBottom = myYScale(d[key + '_y0']);
              const barH = barBottom - barTop;
              stackTotal += d[key];
              if (barTop < stackTopY) stackTopY = barTop;
              if (dlShowValue && barH >= dlFontSize + 2) {
                const barCenterY = barTop + barH / 2;
                parent.append('text').attr('class', 'barLabel')
                  .attr('x', barX + margins.left)
                  .attr('y', barCenterY + margins.top + dlFontSize / 3)
                  .attr('text-anchor', 'middle')
                  .attr('fill', dlColor)
                  .attr('font-family', 'Poppins')
                  .attr('font-size', dlFontSize + 'px')
                  .attr('font-weight', dlFontWeight)
                  .text(d3.format(dlFormat)(d[key]));
              }
            });
            // total on top
            if (dlShowTotal && stackTotal > 0) {
              parent.append('text').attr('class', 'barLabel')
                .attr('x', myXScale(d.date) + margins.left)
                .attr('y', stackTopY + margins.top - 5)
                .attr('text-anchor', 'middle')
                .attr('fill', dlTotalColor)
                .attr('font-family', 'Poppins')
                .attr('font-size', dlFontSize + 'px')
                .attr('font-weight', dlTotalFontWeight)
                .text(d3.format(dlFormat)(stackTotal));
            }
          }
        });
      }

      // ---- LINE DATA LABELS ----
      lineScaleKeys.forEach((key: string) => {
        const labelsGroup = chartGroup.select('.lineLabelsGroup_' + key);
        labelsGroup.selectAll('.lineLabel').remove();
        if (!dlShowLine || areaKeys.indexOf(key) > -1 || myChart.hiddenKeys.has(key)) return;
        labelsGroup.attr('transform', (d: any) => 'translate(0,' + (d === 'brush' ? (margins.mid + chartHeight) : 0) + ')');

        const labelData = chartData.filter((f: any) => f[key] !== null && f[key] !== undefined);
        labelsGroup.selectAll('.lineLabel_' + key)
          .data((d: any) => {
            const myData = JSON.parse(JSON.stringify(labelData));
            myData.map((n: any) => { n.date = new Date(n.date); n.type = d; });
            return myData;
          })
          .join((enter: any) => enter.append('text').attr('class', 'lineLabel lineLabel_' + key))
          .attr('x', (d: any) => (d.type === 'brush' ? xScaleAll(d.date) : xScale(d.date)) + margins.left)
          .attr('y', (d: any) => (d.type === 'brush' ? yLineScaleBrush(d[key]) : yLineScale(d[key])) + margins.top - circleRadius - 5)
          .attr('text-anchor', 'middle')
          .attr('fill', dlColor)
          .attr('font-family', 'Poppins')
          .attr('font-size', (d: any) => (d.type === 'brush' ? 0 : dlFontSize) + 'px')
          .attr('font-weight', dlFontWeight)
          .text((d: any) => d3.format(dlFormat)(d[key]));
      });

      // ---- AREA DATA LABELS ----
      mySvg.selectAll('.areaLabel').remove();
      if (dlShowArea && areaKeys.length > 0) {
        chartData.forEach((d: any) => {
          if (isStackedArea) {
            // stacked area: show each segment's value centered, plus total on top
            let areaTotal = 0;
            let areaTopY = Infinity;
            areaKeys.forEach((key: string) => {
              if (myChart.hiddenKeys.has(key) || d[key + '_y1'] == null || d[key] === null || d[key] === undefined || d[key] === 0) return;
              const areaTop = yLineScale(d[key + '_y1']);
              const areaBottom = yLineScale(d[key + '_y0']);
              const areaH = areaBottom - areaTop;
              areaTotal += d[key];
              if (areaTop < areaTopY) areaTopY = areaTop;
              if (dlShowValue && areaH >= dlFontSize + 2) {
                const areaCenterY = areaTop + areaH / 2;
                mySvg.append('text').attr('class', 'areaLabel')
                  .attr('x', xScale(d.date) + margins.left)
                  .attr('y', areaCenterY + margins.top + dlFontSize / 3)
                  .attr('text-anchor', 'middle')
                  .attr('fill', dlColor)
                  .attr('font-family', 'Poppins')
                  .attr('font-size', dlFontSize + 'px')
                  .attr('font-weight', dlFontWeight)
                  .text(d3.format(dlFormat)(d[key]));
              }
            });
            // total on top
            if (dlShowTotal && areaTotal > 0) {
              mySvg.append('text').attr('class', 'areaLabel')
                .attr('x', xScale(d.date) + margins.left)
                .attr('y', areaTopY + margins.top - 5)
                .attr('text-anchor', 'middle')
                .attr('fill', dlTotalColor)
                .attr('font-family', 'Poppins')
                .attr('font-size', dlFontSize + 'px')
                .attr('font-weight', dlTotalFontWeight)
                .text(d3.format(dlFormat)(areaTotal));
            }
          } else {
            // non-stacked area: show value on top of the area line
            areaKeys.forEach((key: string) => {
              if (myChart.hiddenKeys.has(key) || d[key] === null || d[key] === undefined) return;
              mySvg.append('text').attr('class', 'areaLabel')
                .attr('x', xScale(d.date) + margins.left)
                .attr('y', yLineScale(d[key]) + margins.top - 5)
                .attr('text-anchor', 'middle')
                .attr('fill', dlColor)
                .attr('font-family', 'Poppins')
                .attr('font-size', dlFontSize + 'px')
                .attr('font-weight', dlFontWeight)
                .text(d3.format(dlFormat)(d[key]));
            });
          }
        });
      }

      // ---- HOVER LINE setup ----
      const hoverLineProps = myChart.props.hoverLine || { show: false };
      const hoverLine = mySvg.select('#hoverLine' + myClass)
        .attr('y1', margins.top)
        .attr('y2', margins.top + chartHeight)
        .attr('stroke', hoverLineProps.colour || '#B0B0B0')
        .attr('stroke-width', hoverLineProps.width || 1)
        .attr('stroke-dasharray', hoverLineProps.dash || '4,3')
        .attr('pointer-events', 'none')
        .style('opacity', 0);

      // ---- CROSSHAIR GROUP (horizontal dotted lines + value labels) ----
      const crosshairGroup = mySvg.select('#hoverCrosshairGroup' + myClass);
      crosshairGroup.selectAll('*').remove();
      crosshairGroup.style('opacity', 0);

      // crosshair prop: controls which hover types show horizontal guide lines
      // e.g. { bar: true, line: true, area: false }
      const crosshairProps = myChart.props.crosshair || { bar: true, line: true, area: true };
      // crosshairMode:
      //   'all'   → full-plot overlay; hovering anywhere draws a crosshair for
      //             every visible series at that x. Unified tooltip. (default)
      //   'hover' → crosshair only when the mouse is directly over a bar, line
      //             dot, or area — and shows only that single hovered series.
      const crosshairMode: 'hover' | 'all' = myChart.props.crosshairMode === 'hover' ? 'hover' : 'all';

      const isPercentChart = (myChart.props.percentageMode || 'none') !== 'none';
      const hoverSuffixPrimary = isPercentChart ? '%' : (myChart.props.yAxisSuffix || '');
      const hoverSuffixSecondary = myChart.props.yAxisRightSuffix || '';

      // determine which keys use right axis
      const rightAxisKeySet = new Set(
        (isStackedArea || lineOnPrimary) ? [] : [...lineKeys, ...areaKeys.filter((k: string) => lineKeys.indexOf(k) === -1)]
      );

      // formats a number the same way the crosshair label does
      function formatCrosshairValue(val: number, side: 'left' | 'right' = 'left'): string {
        const isSecondary = side === 'right';
        if (isPercentChart && !isSecondary) {
          return d3.format(',.1f')(val) + '%';
        }
        const suffix = isSecondary ? hoverSuffixSecondary : hoverSuffixPrimary;
        return (val < 1 && val > 0 ? val.toFixed(1) : (Number.isInteger(val) ? d3.format(',')(val) : d3.format(',.1f')(val))) + suffix;
      }

      // helper: draw a single horizontal dotted line from data point to axis with value label.
      // `val` is the value the Y line is drawn at (for stacked bars this is the cumulative
      // stack top); `segmentVal` is the optional raw segment value shown in parens before it,
      // so a stacked bar segment of 300 sitting at axis value 4500 reads "(300) 4,500".
      function drawCrosshair(
        xPos: number,
        val: number | null | undefined,
        yScaleUsed: any,
        colour: string,
        side: 'left' | 'right',
        segmentVal?: number | null,
      ): void {
        if (val === null || val === undefined || isNaN(val)) return;
        const yPos = yScaleUsed(val) + margins.top;
        const axisX = side === 'left' ? margins.left : margins.left + plotWidth;

        // horizontal dotted line from axis to data point x
        crosshairGroup.append('line')
          .attr('x1', axisX)
          .attr('y1', yPos)
          .attr('x2', xPos)
          .attr('y2', yPos)
          .attr('stroke', colour)
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,3')
          .attr('pointer-events', 'none');

        // value label at the axis end — prepend segment value in parens when
        // it differs from the stacked total (stacked bars only).
        const totalFormatted = formatCrosshairValue(val, side);
        const showSegment =
          segmentVal !== undefined &&
          segmentVal !== null &&
          !isNaN(segmentVal) &&
          segmentVal !== val;
        const formatted = showSegment
          ? `(${formatCrosshairValue(segmentVal as number, side)}) ${totalFormatted}`
          : totalFormatted;

        // Push 9px outward from the axis (4px base + 5px extra) so the
        // tooltip pill clears the brush/range slider on either side.
        const labelX = side === 'left' ? axisX - 9 : axisX + 9;
        const anchor = side === 'left' ? 'end' : 'start';

        const textNode = crosshairGroup.append('text')
          .attr('x', labelX)
          .attr('y', yPos + 4)
          .attr('text-anchor', anchor)
          .attr('font-family', 'Poppins, sans-serif')
          .attr('font-size', '10px')
          .attr('font-weight', '600')
          .attr('fill', '#ffffff')
          .attr('pointer-events', 'none')
          .text(formatted);

        // bg rect behind the label — tooltip-style black pill with white text
        const bbox = (textNode.node() as SVGTextElement).getBBox();
        const padX = 6;
        const padY = 3;
        crosshairGroup.insert('rect', 'text:last-of-type')
          .attr('x', bbox.x - padX)
          .attr('y', bbox.y - padY)
          .attr('width', bbox.width + padX * 2)
          .attr('height', bbox.height + padY * 2)
          .attr('rx', 3)
          .attr('fill', '#000000')
          .attr('opacity', 0.8)
          .attr('pointer-events', 'none');
      }

      // hoverType: 'bar' | 'line' | 'area' | 'chart'
      //   'chart' = universal hover from the full-plot overlay; draws a
      //   crosshair for every visible series at the hovered x.
      // hoveredKey: specific key being hovered (e.g. from bar class or line dot class)
      function showHoverLine(d: any, hoverType?: string, hoveredKey?: string): void {
        if (!hoverLineProps.show || !d || !d.date) return;
        const scale = d.type === 'brush' ? xScaleAll : xScale;
        const xPos = scale(new Date(d.date)) + margins.left;
        hoverLine.attr('x1', xPos).attr('x2', xPos)
          .style('opacity', 1);

        // clear previous crosshair elements
        crosshairGroup.selectAll('*').remove();

        // check if crosshair is enabled for this hover type
        const crosshairEnabled = hoverType === 'chart'
          ? !!(crosshairProps.bar || crosshairProps.line || crosshairProps.area)
          : !!(hoverType && crosshairProps[hoverType]);
        if (!hoverType || !crosshairEnabled) {
          crosshairGroup.style('opacity', 0);
          return;
        }

        // helpers for drawing each series type
        const drawBarKey = (key: string): void => {
          const val = d[key];
          if (val === null || val === undefined || isNaN(val)) return;
          const isRight = hasDualAxis && rightAxisKeySet.has(key);
          const yS = isRight ? yScaleRight : yScale;
          const isStackedBar = barMode === 'stacked' && d[key + '_y1'] != null;
          const yVal = isStackedBar ? d[key + '_y1'] : val;
          const segmentVal = isStackedBar ? val : undefined;
          drawCrosshair(xPos, yVal, yS, myChart.props.colours[key] || '#333', isRight ? 'right' : 'left', segmentVal);
        };
        const drawLineKey = (key: string): void => {
          const val = d[key];
          if (val === null || val === undefined || isNaN(val)) return;
          const isRight = hasDualAxis && rightAxisKeySet.has(key);
          drawCrosshair(xPos, val, isRight ? yScaleRight : yScale, myChart.props.colours[key] || '#333', isRight ? 'right' : 'left');
        };
        const drawAreaKey = (key: string): void => {
          const isRight = hasDualAxis && rightAxisKeySet.has(key);
          const yS = isRight ? yScaleRight : yScale;
          const yVal = (isStackedArea && d[key + '_y1'] != null) ? d[key + '_y1'] : d[key];
          if (yVal === null || yVal === undefined || isNaN(yVal)) return;
          drawCrosshair(xPos, yVal, yS, myChart.props.colours[key] || '#333', isRight ? 'right' : 'left');
        };

        if (hoverType === 'bar' && hoveredKey) {
          drawBarKey(hoveredKey);
        } else if (hoverType === 'line' && hoveredKey) {
          drawLineKey(hoveredKey);
        } else if (hoverType === 'area') {
          const areaOnlyKeysLocal = areaKeys.filter((k: string) => lineKeys.indexOf(k) === -1);
          areaOnlyKeysLocal.forEach(drawAreaKey);
        } else if (hoverType === 'chart') {
          // universal hover — draw crosshair for every visible series
          if (crosshairProps.bar !== false)  barKeys.forEach(drawBarKey);
          if (crosshairProps.line !== false) lineKeys.forEach(drawLineKey);
          if (crosshairProps.area !== false) {
            const areaOnlyKeysLocal = areaKeys.filter((k: string) => lineKeys.indexOf(k) === -1);
            areaOnlyKeysLocal.forEach(drawAreaKey);
          }
        } else {
          // fallback: show all keys of the hover type
          if (hoverType === 'bar')  barKeys.forEach(drawBarKey);
          else if (hoverType === 'line') lineKeys.forEach(drawLineKey);
        }

        crosshairGroup.style('opacity', 1);
      }
      function hideHoverLine(): void {
        hoverLine.style('opacity', 0);
        crosshairGroup.style('opacity', 0);
      }

      // ---- MOUSEOVER (bars + dots) ----
      mySvg.selectAll('.barRect')
        .on('mouseover', function (this: any, event: any, d: any) {
          if (d && d.type !== 'brush') {
            // detect which bar key from the element's class (barRect_<key>)
            const classList = (this.getAttribute('class') || '');
            let hoveredKey = '';
            barKeys.forEach((key: string) => {
              if (classList.indexOf('barRect_' + key) !== -1) hoveredKey = key;
            });
            showHoverLine(d, 'bar', hoveredKey);
            myChart.showTooltip('dynamicBar', d, event.pageX, event.pageY, width);
          }
        })
        .on('mouseout', () => { hideHoverLine(); myChart.hideTooltip('dynamicBar'); })
        .on('click', (event: any, d: any) => {
          if (d && d.type !== 'brush') {
            const xVar = myChart.config?.xAxisVar || '';
            if (xVar && d[xVar] !== undefined) {
              myChart.barClicked.emit({ column: xVar, value: d[xVar] });
            }
          }
        });

      mySvg.selectAll('[class*="lineDot_"]')
        .on('mouseover', function (this: any, event: any, d: any) {
          if (d && d.type !== 'brush') {
            // detect which line key from the element's class (lineDot_<key>)
            const classList = (this.getAttribute('class') || '');
            let hoveredKey = '';
            lineKeys.forEach((key: string) => {
              if (classList.indexOf('lineDot_' + key) !== -1) hoveredKey = key;
            });
            showHoverLine(d, 'line', hoveredKey);
            myChart.showTooltip('lineDot', d, event.pageX, event.pageY, width);
          }
        })
        .on('mouseout', () => { hideHoverLine(); myChart.hideTooltip('lineDot'); });

      // ---- UNIVERSAL HOVER OVERLAY ----
      // A full-plot invisible rect catches mousemove everywhere (including the
      // gaps between bars and between line dots), so the crosshair + tooltip
      // work for every chart combination (bar-only, line-only, area-only,
      // bar+line, bar+area, line+area, bar+line+area). It draws a single
      // crosshair for *every* visible series via hoverType='chart'.
      //
      // In crosshairMode='hover' the overlay is only created when there are
      // area keys (legacy behavior) so that per-bar and per-line-dot mouseovers
      // can still fire natively and show a single-series crosshair.
      if (crosshairMode === 'all' || areaKeys.length > 0) {
        const bisectDate = d3.bisector((d: any) => d.date).left;
        // remove previous overlay if any
        mySvg.select('.areaHoverOverlay_' + myClass).remove();
        // select the 'chart' group (last .chartGroup), not the 'brush' group
        const allChartGroups = mySvg.selectAll('.chartGroup' + myClass);
        const chartGroupEl = d3.select(allChartGroups.nodes()[allChartGroups.size() - 1] as any);
        const overlayRect = chartGroupEl.append('rect')
          .attr('class', 'areaHoverOverlay_' + myClass)
          .attr('x', 0).attr('y', 0)
          .attr('width', plotWidth)
          .attr('height', chartHeight)
          .attr('transform', 'translate(' + margins.left + ',' + margins.top + ')')
          .style('fill', 'none')
          .style('pointer-events', 'all');

        // In 'hover' mode the overlay must sit BELOW bars and line dots so
        // that those elements receive their own native mouseover events and
        // fire single-series crosshairs. The overlay still catches mousemove
        // in the empty gaps (and over area fills, since area paths use
        // pointer-events:none). In 'all' mode we keep the overlay on top so
        // it blankets the whole plot and drives the unified crosshair.
        if (crosshairMode === 'hover') {
          overlayRect.lower();
        }

        overlayRect
          .on('mousemove', (event: any) => {
            const [mx] = d3.pointer(event, chartGroupEl.node());
            const xDate = xScale.invert(mx - margins.left);
            const idx = bisectDate(chartData, xDate, 1);
            const d0 = chartData[idx - 1];
            const d1 = chartData[idx];
            if (!d0 && !d1) return;
            const closest = !d1 ? d0 : !d0 ? d1 :
              (+xDate - +d0.date > +d1.date - +xDate ? d1 : d0);
            const hoverData = { ...closest, type: 'chart' };
            // 'all' → crosshair for every visible series at this x
            // 'hover' → overlay only exists for areas; draw single-series area crosshair (legacy)
            showHoverLine(hoverData, crosshairMode === 'all' ? 'chart' : 'area');
            myChart.showTooltip('dynamicBar', hoverData, event.pageX, event.pageY, width);
          })
          .on('mouseout', () => {
            hideHoverLine();
            myChart.hideTooltip('dynamicBar');
          })
          .on('click', (event: any) => {
            // preserve bar-click drilldown even though the overlay sits on top
            const [mx] = d3.pointer(event, chartGroupEl.node());
            const xDate = xScale.invert(mx - margins.left);
            const idx = bisectDate(chartData, xDate, 1);
            const d0 = chartData[idx - 1];
            const d1 = chartData[idx];
            if (!d0 && !d1) return;
            const closest = !d1 ? d0 : !d0 ? d1 :
              (+xDate - +d0.date > +d1.date - +xDate ? d1 : d0);
            const xVar = myChart.config?.xAxisVar || '';
            if (xVar && closest && closest[xVar] !== undefined) {
              myChart.barClicked.emit({ column: xVar, value: closest[xVar] });
            }
          });
      }
    }

    // =========================================================
    // brushed()
    // =========================================================
    function brushed(event: any): void {
      if (!brushReady) return;
      const selection = event.selection;
      if (selection !== null) {
        myChart.hideTooltip('dynamicBar');
        const xDomain: any = selection.map(xScaleAll.invert);
        const dataRemaining: any = chartData.filter((f: any) => f.date >= xDomain[0] && f.date <= xDomain[1]);
        if (dataRemaining.length < 1) {
          const previousStart = xScaleAll(xScale.domain()[0]);
          const previousEnd = xScaleAll(xScale.domain()[1]);
          if (previousStart !== undefined && previousEnd !== undefined) {
            mySvg.select('#brushGroup' + myClass)
              //@ts-ignore
              .call(brush.move, myChart.props.showBrush === false ? null : [previousStart, previousEnd]);
          }
        } else {
          // partial period visibility
          if (String(xDomain[1]) === String(xScaleAll.domain()[1])) {
            mySvg.select('.partialPeriodLine' + myClass)
              .attr('visibility', myChart.props.partialPeriod === true ? 'visible' : 'hidden');
            mySvg.select('.partialPeriodLineLabel' + myClass)
              .attr('visibility', myChart.props.partialPeriod === true ? 'visible' : 'hidden');
          } else {
            mySvg.select('.partialPeriodLine' + myClass).attr('visibility', 'hidden');
            mySvg.select('.partialPeriodLineLabel' + myClass).attr('visibility', 'hidden');
          }
          xScale.domain(xDomain);
          mySvg.select('#xAxis' + myClass)
            .call(
              d3.axisBottom(xScale)
                .tickSizeOuter(0).tickFormat(xTickFormat).ticks(tickCount)
                .tickValues(tickValues === null ? null : tickValues.filter((f: any) => f >= xDomain[0] && f <= xDomain[1])))
            .attr('transform', 'translate(' + margins.left + ',' + (margins.top + chartHeight) + ')');
          drawChart();
        }
      }
    }

    // =========================================================
    // Helper functions
    function measureWidth(myText: any, myFontSize: any): any {
      const context: any = document.createElement('canvas').getContext('2d');
      context.font = myFontSize + 'px Poppins, sans-serif';
      return context.measureText(myText).width;
    }

    function wrap(text: any, width: any): void {
      text.selectAll('tspan').remove();
      let lineNumber = 0;
      const originalValue = text.text();
      text.each(function (): any {
        // @ts-ignore
        const text = d3.select(this);
        const words = text.text().split(/\s+/).reverse();
        let word = null;
        let line: any = [];
        const lineHeight = 1.1;
        const dy = 0;
        let tspan = text.text(null).append('tspan').attr('x', 0).attr('y', 0).attr('dy', dy + 'em');
        while (word = words.pop()) {
          line.push(word);
          tspan.text(line.join(' '));
          // @ts-ignore
          if (tspan.node().getComputedTextLength() > width) {
            line.pop();
            tspan.text(line.join(' '));
            line = [word];
            if (word.trim().length > 0) {
              tspan = text.append('tspan').attr('x', 0).attr('y', 0).attr('dy', ++lineNumber * lineHeight + dy + 'em').text(word);
            }
          }
        }
      });
      if (lineNumber > 1) {
        text.selectAll('tspan').remove();
        text.attr('id', originalValue);
        text.text(originalValue.substr(0, 5) + '..');
      }
    }

    function getMaxTextWidth(): number {
      let maxTextWidth = 0;
      mySvg.selectAll('#xAxis' + myClass + ' text').each(function () {
        // @ts-ignore
        const myNode = d3.select(this).node().getBBox();
        maxTextWidth = Math.max(maxTextWidth, myNode.width);
      });
      return maxTextWidth;
    }

    // =========================================================
    // D3 Range Slider Functions (generic — works for both axes)
    // =========================================================
    function createRangeSlider(
      id: string, min: number, max: number, side: string,
      targetScale: any, axisSelector: string,
      getMin: () => number, setMin: (v: number) => void,
      getMax: () => number, setMax: (v: number) => void,
      accentColour: string
    ): void {
      const sliderGroupId = 'sliderGroup-' + id;
      const sliderClass = id + '-slider';
      const xPos = side === 'left' ? margins.left : margins.left + plotWidth;

      slider.select('#' + sliderGroupId).remove();
      const sliderGroup = slider.append('g').attr('id', sliderGroupId);

      // scale: maps data domain → pixel range (vertical, top=max bottom=min)
      const scale = d3.scaleLinear()
        .domain([min, max])
        .range([targetScale.range()[0], 0])
        .clamp(true);

      // drag behaviours
      const dragMinHandle = d3.drag().on('drag', (event: any) =>
        onDragHandle(sliderGroup, event, scale, sliderClass, id, min, max, 'min', side, targetScale, axisSelector, getMin, setMin, getMax, setMax));
      const dragMaxHandle = d3.drag().on('drag', (event: any) =>
        onDragHandle(sliderGroup, event, scale, sliderClass, id, min, max, 'max', side, targetScale, axisSelector, getMin, setMin, getMax, setMax));

      // track line (background)
      sliderGroup.append('line')
        .attr('class', sliderClass + ' ' + id + '-track')
        .attr('y1', scale(min)).attr('y2', scale(max))
        .attr('transform', 'translate(' + xPos + ',' + margins.top + ')')
        .attr('stroke', '#E8EAEE')
        .attr('stroke-width', 3)
        .attr('stroke-linecap', 'round');

      // track inset (selected range highlight)
      sliderGroup.append('line')
        .attr('class', sliderClass + ' ' + id + '-track-inset')
        .attr('y1', scale(getMin())).attr('y2', scale(getMax()))
        .attr('transform', 'translate(' + xPos + ',' + margins.top + ')')
        .attr('stroke', accentColour)
        .attr('stroke-width', 3)
        .attr('stroke-linecap', 'round');

      // create min and max handles
      createSliderHandle(sliderGroup, sliderClass, id, 'min', getMin(), scale, dragMinHandle, xPos, accentColour);
      createSliderHandle(sliderGroup, sliderClass, id, 'max', getMax(), scale, dragMaxHandle, xPos, accentColour);
    }

    function createSliderHandle(sliderGroup: any, sliderClass: string, id: string, handleType: string, value: number, scale: any, dragHandle: any, xPos: number, accentColour: string): void {
      const circleColor = accentColour;

      // hover glow layer
      const hoverLayer = sliderGroup.append('circle')
        .attr('class', sliderClass + ' ' + id + '-' + handleType + '-hover-layer')
        .attr('cy', scale(value))
        .attr('transform', 'translate(' + xPos + ',' + margins.top + ')')
        .attr('r', myChart.handleRadius + 8)
        .attr('fill', circleColor)
        .attr('opacity', 0)
        .attr('pointer-events', 'none');

      // draggable handle
      const handle = sliderGroup.append('circle')
        .attr('class', sliderClass + ' ' + id + '-' + handleType + '-handle')
        .attr('cy', scale(value))
        .attr('transform', 'translate(' + xPos + ',' + margins.top + ')')
        .attr('r', myChart.handleRadius)
        .attr('fill', circleColor)
        .attr('stroke', 'white')
        .attr('stroke-width', 2)
        .style('cursor', 'ns-resize')
        .on('mousedown', function () { handle.raise(); })
        .call(dragHandle);

      handle.on('mouseover', function () {
        hoverLayer.transition().duration(200).attr('opacity', 0.4);
      })
      .on('mouseout', function () {
        hoverLayer.transition().duration(400).attr('opacity', 0);
      });
    }

    function onDragHandle(
      sliderGroup: any, event: any, scale: any, sliderClass: string, id: string,
      min: number, max: number, handleType: string, side: string,
      targetScale: any, axisSelector: string,
      getMin: () => number, setMin: (v: number) => void,
      getMax: () => number, setMax: (v: number) => void
    ): void {
      const pos = event.y - margins.top;
      // gap is 5% of range (min 1) so it works for small ranges like 0–10
      const gap = Math.max(1, (max - min) * 0.05);

      if (handleType === 'min') {
        const clampedPos = Math.min(scale(min), Math.max(scale(max), pos));
        const newMin = scale.invert(clampedPos);
        if (newMin >= min && newMin <= getMax() - gap) {
          setMin(newMin);
          updateSliderHandlePosition(sliderGroup, sliderClass, id, 'min', clampedPos);
          updateTrackInset(sliderGroup, scale, sliderClass, id, newMin, getMax());
          targetScale.domain([newMin, getMax()]);
          updateAxis(targetScale, side, axisSelector);
          drawChart();
        }
      } else {
        const clampedPos = Math.max(scale(max), Math.min(scale(min), pos));
        const newMax = scale.invert(clampedPos);
        if (newMax <= max && newMax >= getMin() + gap) {
          setMax(newMax);
          updateSliderHandlePosition(sliderGroup, sliderClass, id, 'max', clampedPos);
          updateTrackInset(sliderGroup, scale, sliderClass, id, getMin(), newMax);
          targetScale.domain([getMin(), newMax]);
          updateAxis(targetScale, side, axisSelector);
          drawChart();
        }
      }
    }

    function updateAxis(targetScale: any, side: string, axisSelector: string): void {
      if (side === 'left') {
        mySvg.select(axisSelector)
          //@ts-ignore
          .call(d3.axisLeft(targetScale).tickSizeOuter(0).ticks(isPercentAxis ? 6 : 7)
            .tickValues(isPercentAxis ? [0, 20, 40, 60, 80, 100].filter((v: number) => { const dom = targetScale.domain(); return v >= dom[0] && v <= dom[1]; }) as any : null as any)
            .tickFormat((d: any) => {
              if (isPercentAxis) {
                const fmt = myChart.props.yAxisFormatting || '.0f';
                return d3.format(fmt)(d) + '%';
              }
              return d3.format(myChart.props.yAxisFormatting)(d) + (myChart.props.yAxisSuffix || '');
            }));
      } else {
        mySvg.select(axisSelector)
          .call(d3.axisRight(targetScale).tickSizeOuter(0).ticks(7)
            .tickFormat((d: any) => d3.format(myChart.props.yAxisRightFormatting || '-2~s')(d) + (myChart.props.yAxisRightSuffix || '')));
      }
    }

    function updateTrackInset(sliderGroup: any, scale: any, sliderClass: string, id: string, min: number, max: number): void {
      sliderGroup.select('.' + sliderClass + '.' + id + '-track-inset')
        .attr('y1', scale(min)).attr('y2', scale(max));
    }

    function updateSliderHandlePosition(sliderGroup: any, sliderClass: string, id: string, handleType: string, newPos: number): void {
      sliderGroup.select('.' + sliderClass + '.' + id + '-' + handleType + '-handle').attr('cy', newPos);
      sliderGroup.select('.' + sliderClass + '.' + id + '-' + handleType + '-hover-layer').attr('cy', newPos);
    }
  }

  getChartData(): void {
      this.chartData = this.data;
      if (!this.chartData || this.chartData.length === 0) {
      this.isLoading = false;
      this.noData = true;
      return;
    }
    const cfg= this.item.config;

    if(cfg?.AggregationEnabled == true){
      const { groups, summary } = this.getAvgBusinessHours(this.data, cfg);
      this.chartData =groups
      console.log('Avg Business Hours Chart Data', this.chartData, summary);
    }else{
    this.chartData = this.data;
    }
    debugger

    this.props={
      colours: cfg.colours || {},
      legendLabels: cfg.legendLabels || {},
      barKeys: cfg.barKeys ? (Array.isArray(cfg.barKeys) ? cfg.barKeys : cfg.barKeys.split(',').filter((k: string) => k)) : [],
      lineKeys: cfg.lineKeys ? (Array.isArray(cfg.lineKeys) ? cfg.lineKeys : cfg.lineKeys.split(',').filter((k: string) => k)) : [],
      areaKeys: cfg.areaKeys ? (Array.isArray(cfg.areaKeys) ? cfg.areaKeys : cfg.areaKeys.split(',').filter((k: string) => k)) : [],
      barMode: cfg.barMode || 'none',
      stackedArea:cfg.stackedArea == true || false,
      percentageChart: cfg.percentageChart === 'true' || cfg.percentageChart === true || false,
      // percentageMode: 'none' | 'ofMax' | 'ofTotal' | 'actual'
      // 'none'    → raw values (default)
      // 'ofMax'   → each value / global max * 100 (legacy percentageChart behaviour)
      // 'ofTotal' → each value / row total * 100 (normalized 100% stacked bars)
      // 'actual'  → data already contains percentages, display as-is with % and 0-100 axis
      percentageMode: cfg.percentageMode || (cfg.percentageChart === 'true' || cfg.percentageChart === true ? 'ofTotal' : 'none'),
      lineOnPrimaryAxis: cfg.lineOnPrimaryAxis === 'true' || cfg.lineOnPrimaryAxis === true || false,
      period: this.activePeriod,
      xAxisLabel: cfg.xAxisLabel || 'period',
      yAxisLabel: cfg.yAxisLabel || '',
      yAxisRightLabel: cfg.yAxisRightLabel || '',
      yAxisLabelColour: cfg.yAxisLabelColour || '#737D88',
      yAxisRightLabelColour: cfg.yAxisRightLabelColour || '#737D88',
      xAxisVar: cfg.xAxisVar || 'period',
      showBrush: cfg.showBrush === 'true' || cfg.showBrush === true,
      d3AxisFormatting: cfg.d3AxisFormatting === 'true' || cfg.d3AxisFormatting === true,
      yAxisFormatting: cfg.yAxisFormatting || '-2~s',
      yAxisSuffix: cfg.yAxisSuffix || '',
      yAxisRightFormatting: cfg.yAxisRightFormatting || '-2~s',
      yAxisRightSuffix: cfg.yAxisRightSuffix || '',
      axisFontSize: Number(cfg.axisFontSize) || 12,
      axisFontWeight: Number(cfg.axisFontWeight) || 700,
      chartHeight: Number(cfg.chartHeight) || 500,
      partialPeriod: cfg.partialPeriod === 'true' || cfg.partialPeriod === true,
      slider: cfg.slider === 'true' || cfg.slider === true,
      animate: cfg.animate === 'true' || cfg.animate === true,
      barRadius:  { value: 4, mode: 'eachTop' },
      hoverLine: { show: true, colour: '#B0B0B0', width: 1, dash: '4,3' },
      crosshair: { bar: true, line: true, area: true },
      // 'all'   → crosshair for every visible series, hover anywhere in the plot
      // 'hover' → crosshair only for the bar / dot / area under the cursor
      crosshairMode:'hover', //cfg.crosshairMode === 'hover' ? 'hover' : 'all',
      lineDot: cfg.lineDot || { show: true, radius: 4, fill: 'white', strokeWidth: 2 },
      dataLabels: cfg.dataLabels || { bar: false, line: false, area: false, total: false, value: false, fontSize: 11, fontWeight: 600, totalFontWeight: 700, color: '#333', totalColor: '#333', format: '-2~s' }
    }
    console.log(`Chart Props:${cfg.heading}`, this.props);
    console.log('Chart Data', this.chartData);

    this.detectPartialPeriod();
   
    this.plotChart();
    this.noData = false;
    this.isLoading = false; 
  }
    
  

  private detectPartialPeriod(): void {
    if (this.chartData.length > 0) {
      if (Object.keys(this.chartData[0]).indexOf('partial_period_flg') > -1) {
        if (this.chartData.find((f: any) => f['partial_period_flg'] === 'Y') !== undefined) {
          this.props.partialPeriod = true;
        }
      }
    }
  }
}
